import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  IconEdit16,
  IconDelete16,
  IconView16,
  CircularLoader,
  IconClock16,
  IconCheckmark16,
  IconInfo16,
  colors, IconFilter16, Checkbox, Popover,
} from '@dhis2/ui';
import { useDataEngine } from '@dhis2/app-runtime';
import { usePerformanceContext } from '../PerformanceManagement';
import { useAuth } from '../../auth/AuthContext';
import { getAuthHeader } from '../../../utils/auth';
import {
  DATA_ELEMENTS,
  ORG_GOALS_PROGRAM, ORG_GOALS_STAGE,
  getDataValue,
} from '../api/GoalsConstants';
import { POSITION_ATTRIBUTES } from '../../staff/api/constants';
import { useObjectiveWeight } from '../hooks/useObjectiveWeight';
import { InputField, IconSearch24, IconCross24 } from '@dhis2/ui';
import ObjectiveViewModal from '../components/ObjectiveViewModal';
import AddObjectiveButton from '../utils/AddObjectiveButton';
import { isWithinFiscalYear, isFiscalYearActive, getFiscalYearLabel } from '../../GoalsManagement/utils/fiscalYearHelpers';
import { ClipboardCheckIcon, NotebookIcon, UserRoundIcon, UsersRoundIcon } from 'lucide-react';
import { GiNotebook, GiTargetArrows, GiTargeted } from 'react-icons/gi';
import { useFiscalYear } from '../../../shared/FiscalYearContext';

// Constants
const APPROVAL_STATUS = {
  APPROVED: 'approved',
  RETURNED: 'returned',
  PENDING: 'pending',
};

// Reporting To Attribute from database
const REPORTING_TO = 'SjP47qi3JeB';

// ✅ Helper: Extract attributes into a map
const extractAttributes = (attributes = []) => {
  const map = {};
  attributes.forEach((attr) => {
    map[attr.attribute] = attr.value;
  });
  return map;
};


// ✅ Staff list state
let matchedStaffList = [];

// ✅ Get staff from position
const getPositionStaff = (position, staffList) => {
  if (!position) return null;
  const attributes = extractAttributes(position.attributes);
  const staffId = attributes[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
  const staff = staffList.find((s) => s.id === staffId);
  if (staff) return staff;
  return staffId
    ? {
      id: staffId,
      firstName: 'Unknown',
      surname: 'Staff',
      userCredentials: { username: 'unknown' },
    }
    : null;
};


const getObjectivesByPosition = (position, events, staffList = []) => {
  if (!position) return [];

  const positionTEI = position.trackedEntityInstance;

  return events.filter(event => {
    // Method 1: Check trackedEntityInstance directly
    if (event.trackedEntityInstance === positionTEI) return true;

    // Method 2: Check ALL data values for position TEI
    const hasPositionRef = event.dataValues?.some(dv =>
      dv.value === positionTEI
    );

    return hasPositionRef;
  }).map(event => {
    const dataValues = event.dataValues || [];
    const category = dataValues.find(dv => dv.dataElement === 'uBPW95V2VoX')?.value || 'Not set';

    // Extract staff info if needed
    const staffId = dataValues.find(dv => dv.dataElement === 'V66IFsw5kq6')?.value; // Current incumbent
    const staff = staffList.find(s => s.id === staffId);

    return {
      id: event.event,
      name: dataValues.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed',
      description: dataValues.find(dv => dv.dataElement === 'nWqW5BrcgP1')?.value || '',
      orgUnit: event.orgUnit,
      startDate: dataValues.find(dv => dv.dataElement === 'eDcVy661j9e')?.value || '',
      completionDate: dataValues.find(dv => dv.dataElement === 'J0rjxrDKn1l')?.value || '',
      status: dataValues.find(dv => dv.dataElement === 'DL7KxNhiV0x')?.value || '',
      approvalStatus: dataValues.find(dv => dv.dataElement === 'DzQorireRBf')?.value || 'pending',
      approvalRemark: dataValues.find(dv => dv.dataElement === 'KeGWxm1IDYF')?.value || '', // DEBUG: Extract remarks
      dataValues: dataValues,
      position: position,
      linkedStaff: staff,
      category: category,
    };
  });
};


const ObjectivesList = ({ title = 'Performance Objectives', onAddObjective, kraEnabled = true, }) => {
  const engine = useDataEngine();
  const { user } = useAuth();
  const {
    events = [],
    currentUserPosition,
    reportingToPosition,
    directReportPositions,
    reportingStructureLoaded,
    staffList,
    onEdit,
    onDelete,
    setLoading,
    setData,
    editingLevelId,
    loadingEvents,
    loadEvents,
    setEvents,
  } = usePerformanceContext();

  //const [localFiscalYear, setLocalFiscalYear] = useState({ start: '', end: '' });
  //const [fiscalYearLoaded, setFiscalYearLoaded] = useState(false);

  const { fiscalYear } = useFiscalYear();

  // States
  const [expandedObjectiveId, setExpandedObjectiveId] = useState(null);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [goalItems, setGoalItems] = useState([]);
  const [protectedObjectives, setProtectedObjectives] = useState(new Set());

  const positionTei = currentUserPosition?.trackedEntityInstance;
  const { totalWeight } = useObjectiveWeight(positionTei || null);
  const showWeightAlert = totalWeight !== null && totalWeight !== 100 && totalWeight > 0;

  const [reportingToObjectives, setReportingToObjectives] = useState([]);
  const [directReportObjectives, setDirectReportObjectives] = useState([]);
  const [myObjectives, setMyObjectives] = useState([]);
  
  const [expandedObjectives, setExpandedObjectives] = useState(new Set());

  const [myObjectivesSearch, setMyObjectivesSearch] = useState('');
  const [directReportsSearch, setDirectReportsSearch] = useState('');

  const [goalsFullyLoaded, setGoalsFullyLoaded] = useState(false);

  const [goalLinkages, setGoalLinkages] = useState({});
  const [loadingLinkages, setLoadingLinkages] = useState(false);
  const [isLoadingObjective, setIsLoadingObjective] = useState(false);

  const [managerObjectivesLoaded, setManagerObjectivesLoaded] = useState(false);
  
  const [childCounts, setChildCounts] = useState({});
  const [viewModalObjective, setViewModalObjective] = useState(null);

  const [selectedGoalFilters, setSelectedGoalFilters] = useState([]);
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const filterButtonRef = useRef(null);

  const [editingPrivateNoteFor, setEditingPrivateNoteFor] = useState(null);
  const [privateNoteValue, setPrivateNoteValue] = useState('');

  const isMounted = useRef(true);
  const [activeTab, setActiveTab] = useState('my'); 
  const [objectivesLoaded, setObjectivesLoaded] = useState(false);

  // Check if the manager (reportingTo) is top-level (has no reporting-to attribute)
  const isManagerTopLevel = useMemo(() => {
    if (!reportingToPosition) return false;
    const attrs = extractAttributes(reportingToPosition.attributes || []);
    return !attrs[REPORTING_TO];
  }, [reportingToPosition]);

  // Approved manager objectives: either explicitly approved, or auto-approved if manager is top-level
  const approvedReportingToObjectives = useMemo(() => {
    return reportingToObjectives.filter(obj => {
      if (isManagerTopLevel) return true; // auto-approved
      return obj.approvalStatus === 'approved';
    });
  }, [reportingToObjectives, isManagerTopLevel]);

  // Creation is allowed only if there are no manager objectives, OR all are approved (or auto-approved)
  const isCreationAllowed = useMemo(() => {
    if (reportingToObjectives.length === 0) return true;
    if (isManagerTopLevel) return true; // all auto-approved
    return reportingToObjectives.every(obj => obj.approvalStatus === 'approved');
  }, [reportingToObjectives, isManagerTopLevel]);

  const allManagerObjectivesApproved = useMemo(() => {
    if (reportingToObjectives.length === 0) return false;
    if (isManagerTopLevel) return true;
    return reportingToObjectives.every(obj => obj.approvalStatus === 'approved');
  }, [reportingToObjectives, isManagerTopLevel]);
  

  const toggleTeamObjective = (objectiveId) => {
    setExpandedObjectives(prev => {
      const newSet = new Set(prev);
      if (newSet.has(`team-${objectiveId}`)) {
        newSet.delete(`team-${objectiveId}`);
      } else {
        newSet.add(`team-${objectiveId}`);
      }
      return newSet;
    });
  };

  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Convert to a nice HSL color (saturation 60%, lightness 45%)
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 60%, 45%)`;
  };


  const clearMySearch = () => setMyObjectivesSearch('');

  const goalNameById = useMemo(() => {
    const map = {};
    goalItems.forEach(goal => {
      if (goal && goal.id) {
        map[goal.id] = goal.name || 'Unnamed Goal';
      }
    });
    return map;
  }, [goalItems]);

  const allObjectivesById = useMemo(() => {
    // Check if we have any objectives before building the map
    const hasObjectives = myObjectives.length > 0 || 
                          directReportObjectives.length > 0 || 
                          reportingToObjectives.length > 0;
    
    if (!hasObjectives) {
      console.log('🔍 [DEBUG] No objectives loaded yet, returning empty map');
      return {};
    }
    
    const all = [
      ...myObjectives,
      ...directReportObjectives,
      ...reportingToObjectives
    ];
    
    const map = {};
    all.forEach(obj => {
      if (obj && obj.id) {
        map[obj.id] = obj;
      }
    });
    
    console.log('🔍 [DEBUG] allObjectivesById created:', {
      totalKeys: Object.keys(map).length,
      sampleKeys: Object.keys(map).slice(0, 5),
      myObjectivesCount: myObjectives.length,
      directReportCount: directReportObjectives.length,
      reportingToCount: reportingToObjectives.length
    });
    
    return map;
  }, [myObjectives, directReportObjectives, reportingToObjectives]);

  // Helper functions to check if expanded

  const getObjectiveGoalId = (objective) => {
      if (!objective?.dataValues) return null;
      
      // Check for direct goal link
      const goalDataValue = objective.dataValues.find(
        dv => dv.dataElement === 'VhrIQQIVpB9'
      );
      
      if (goalDataValue?.value) {
        return goalDataValue.value;
      }
      
      return null;
  };

  // Helper to check if current user can view private note
  const canViewPrivateNote = (objective, currentUserPosition) => {
      if (!objective?.managerPrivateNote) return false;
      if (!currentUserPosition?.trackedEntityInstance) return false;
      // Only the manager who created the note can see it
      return objective.noteCreatedBy === currentUserPosition.trackedEntityInstance;
  };

  // Helper objective lock check 
  const isObjectiveLocked = (objective) => {
      // Check if objective is approved (locked)
      if (objective.approvalStatus === 'approved') {
          return { locked: true, reason: 'This objective has been approved and cannot be modified' };
      }
      
      // Check if it has child objectives (protected)
      if (protectedObjectives.has(objective.id)) {
          const childCount = childCounts[objective.id] || 0;
          return { locked: true, reason: `This objective has ${childCount} aligned objective(s) linked to it and cannot be modified` };
      }
      
      return { locked: false, reason: null };
  };

  const isTeamObjectiveExpanded = (objectiveId) => expandedObjectives.has(`team-${objectiveId}`);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);


  useEffect(() => {
    const loadLinkages = async () => {
      if (myObjectives.length > 0 && goalItems.length > 0) {
        setLoadingLinkages(true);
        const linkages = await loadGoalLinkages();
        setGoalLinkages(linkages);
        setLoadingLinkages(false);
      }
    };

    loadLinkages();
  }, [myObjectives, goalItems, engine]);

  // DELETE LINES 105-149 and replace with:

  useEffect(() => {
    let isMounted = true;

    const loadCompanyGoals = async () => {
    if (!user?.organisationUnits?.[0]?.id || !isMounted) {
      setGoalItems([]);
      setGoalsFullyLoaded(true); // Mark as loaded even if empty
      return;
    }

      console.log('🔍 [OBJECTIVESLIST - GOAL LOADING] Starting...');
      setLoadingGoals(true);

      try {
        // ✅ 1. Get program metadata to find Level 5 org units
        const programResponse = await engine.query({
          programMeta: {
            resource: 'programs',
            params: {
              filter: `id:eq:${ORG_GOALS_PROGRAM}`,
              fields: 'id,name,organisationUnits[id,path,level]'
            }
          }
        });

        const program = programResponse.programMeta?.programs?.[0];
        if (!program) {
          console.warn('⚠️ [OBJECTIVESLIST] Goals program not found');
          if (isMounted) setGoalItems([]);
          return;
        }

        const programOrgUnits = program.organisationUnits || [];
        console.log('📋 [OBJECTIVESLIST] Program org units:', programOrgUnits.length);

        // ✅ 2. Find Level 5 org units (where goals are stored)
        const level5OrgUnits = programOrgUnits.filter(ou => ou.level === 5);

        if (level5OrgUnits.length === 0) {
          console.warn('⚠️ [OBJECTIVESLIST] No Level 5 org units found for goals');
          if (isMounted) setGoalItems([]);
          return;
        }

        // ✅ 3. Use the first Level 5 org unit
        const goalOrgUnitId = level5OrgUnits[0].id;
        console.log('🎯 [OBJECTIVESLIST] Using Level 5 org unit for goals:', goalOrgUnitId);

        // ✅ 4. Load goals from that org unit
        const response = await engine.query({
          goalsQuery: {
            resource: 'events',
            params: {
              program: ORG_GOALS_PROGRAM,
              programStage: ORG_GOALS_STAGE,
              fields: 'event,orgUnit,eventDate,dataValues[dataElement,value]',
              orgUnit: goalOrgUnitId,
              ouMode: 'ACCESSIBLE',
              pageSize: 1000,
            },
          },
        });

        const events = response.goalsQuery?.events || [];
        console.log('✅ [OBJECTIVESLIST] Goals loaded:', events.length);

        // ✅ 5. Debug: Show what we found
        events.forEach((event, i) => {
          const nameDV = event.dataValues?.find(dv => dv.dataElement === 'HJqqfzhH614');
          console.log(`   Goal ${i}: ID=${event.event}, Name="${nameDV?.value || 'MISSING'}"`);
        });

        // ✅ 6. Map goals using the same mapping as ObjectiveForm
        const mappedGoals = events
          .map(event => {
            const dataValuesMap = {};
            event.dataValues?.forEach(dv => {
              dataValuesMap[dv.dataElement] = dv.value;
            });

            return {
              id: event.event,
              name: dataValuesMap['HJqqfzhH614'] || 'Unnamed Goal',
              description: dataValuesMap['zS8QIgZc0Ll'] || '',
              progress: parseInt(dataValuesMap['pOOGDRzEo98']) || 0,
              startDate: dataValuesMap['MH7DQVegD1j'] || '',
              endDate: dataValuesMap['pUvJQ9vy3A3'] || '',
              priority: dataValuesMap['Co2i5BleCcZ'] || '',
              category: dataValuesMap['uBPW95V2VoX'] || '',
              createdBy: dataValuesMap['nZDkNlZwX9I'] || '',
              orgUnit: event.orgUnit,
              eventDate: event.eventDate || '',
            };
          })
          .filter(goal => {
            // Only filter if fiscalYear is properly set with values
            // Otherwise, show all goals (user can filter later)
            const fyReady = fiscalYear?.start && fiscalYear?.end && fiscalYear.start !== '' && fiscalYear.end !== '';
            if (!fyReady) {
              console.log('🔍 [GOAL FILTER - ObjectiveList] Skipping filter - fiscalYear not ready:', fiscalYear);
              return true;
            }
            console.log('🔍 [GOAL FILTER - ObjectiveList] Checking goal:', goal.name, 'eventDate:', goal.eventDate, 'startDate:', goal.startDate, 'endDate:', goal.endDate);
            // Check eventDate first (DHIS2 creation date)
            if (goal.eventDate && isWithinFiscalYear(goal.eventDate, fiscalYear)) {
              console.log('   ✅ eventDate in FY');
              return true;
            }
            // Require BOTH startDate AND endDate to be within FY for goals without eventDate
            const inStart = isWithinFiscalYear(goal.startDate, fiscalYear);
            const inEnd = isWithinFiscalYear(goal.endDate, fiscalYear);
            console.log('   startDate in FY:', inStart, 'endDate in FY:', inEnd);
            return inStart && inEnd;
          });

        if (isMounted) {
          setGoalItems(mappedGoals);
          setGoalsFullyLoaded(true);
          console.log('✅ [OBJECTIVESLIST] Mapped goals:', mappedGoals.length);
        }

      } catch (error) {
        console.error('❌ [OBJECTIVESLIST] Error loading goals:', error);
        if (isMounted) {
          setGoalItems([]);
          setGoalsFullyLoaded(true);
        }
      } finally {
        if (isMounted) {
          setLoadingGoals(false);
        }
      }
    };

    if (reportingStructureLoaded && currentUserPosition) {
      loadCompanyGoals();
    }

    return () => {
      isMounted = false;
    };
  }, [
    user?.organisationUnits?.[0]?.id,
    engine,
    reportingStructureLoaded,
    currentUserPosition,
    ORG_GOALS_PROGRAM,
    ORG_GOALS_STAGE,
    fiscalYear,
  ]);

  
  // Create a more robust function to find goal links
  const findGoalLink = (objective) => {
    const dataValues = objective.dataValues || [];

    // Priority 1: Direct goal link (most reliable)
    const directGoalLink = dataValues.find(dv =>
      dv.dataElement === 'VhrIQQIVpB9' || // Objective Alignment with Company Goals
      dv.dataElement === 'HJqqfzhH614'    // Goal Name field
    );

    if (directGoalLink?.value) {
      return {
        type: 'direct',
        goalId: directGoalLink.value,
        dataElement: directGoalLink.dataElement
      };
    }

    // Priority 2: Parent objective link
    const parentRef = dataValues.find(dv =>
      dv.dataElement === 'plJovUlxfQ0' || // Parent Objective ID
      dv.dataElement === 'neuilg3L4d2' || // Parent Objective ID (alternate)
      dv.dataElement === 'CHp6Bd9CR0Q'    // Linked To Higher Level
    );

    if (parentRef?.value) {
      return {
        type: 'parent',
        parentId: parentRef.value,
        dataElement: parentRef.dataElement
      };
    }

    return null;
  };

  // Modified getLinkedGoalInfo that uses pre-loaded linkages
  const getLinkedGoalInfo = (objective, goalItems = [], goalNameById = {}, allObjectivesById = {}) => {

    // Add this check at the beginning
    if (!objective) {
      return {
        linkedId: null,
        linkedName: null,
        goalName: null,
        isCompanyGoal: false,
        isParentObjective: false,
        isTopLevelObjectiveAsGoal: false
      };
    }

    // Check if objectives are loaded
    if (Object.keys(allObjectivesById).length === 0 && goalItems.length === 0) {
      console.log('⏳ [GOAL INFO] Waiting for data to load...');
      return {
        linkedId: null,
        linkedName: null,
        goalName: null,
        isCompanyGoal: false,
        isParentObjective: false,
        isTopLevelObjectiveAsGoal: false
      };
    }

    if (goalItems.length === 0 || Object.keys(goalNameById).length === 0) {
      console.log('⏳ [GOAL INFO] Goals not loaded yet, skipping...');
      return {
        linkedId: null,
        linkedName: null,
        goalName: null,
        isCompanyGoal: false,
        isParentObjective: false,
        isTopLevelObjectiveAsGoal: false
      };
    }

    console.log('🔍 [GET LINKED GOAL INFO - DEBUG]', {
      userLevel: 'Level ' + (user?.organisationUnits?.[0]?.level || 'unknown'),
      objectiveId: objective?.id,
      objectiveName: objective?.name,
      goalItemsCount: goalItems.length,
      goalNameByIdKeys: Object.keys(goalNameById).length,
      allObjectivesByIdKeys: Object.keys(allObjectivesById).length,
      userOrgUnits: user?.organisationUnits?.map(ou => ({ id: ou.id, name: ou.name, level: ou.level }))
    });

    if (!objective) {
      return {
        linkedId: null,
        linkedName: null,
        goalName: null,
        isCompanyGoal: false,
        isParentObjective: false,
        isTopLevelObjectiveAsGoal: false
      };
    }

    // FIRST: Check for DIRECT COMPANY GOAL link
    const goalDataValue = objective.dataValues?.find(
      dv => dv.dataElement === 'VhrIQQIVpB9'
    );

    if (goalDataValue?.value) {
      const goalId = goalDataValue.value;
      const goalName = goalNameById[goalId];

      console.log('🎯 [FOUND COMPANY GOAL LINK]', {
        objectiveId: objective.id,
        goalId: goalId,
        goalName: goalName
      });

      return {
        linkedId: goalId,
        linkedName: goalName || `Goal ${goalId}`,
        goalName: goalName || `Goal ${goalId}`,
        isCompanyGoal: true,
        isParentObjective: false,
        isTopLevelObjectiveAsGoal: false
      };
    }

    // SECOND: Check for PARENT OBJECTIVE link
    const parentDataElements = ['neuilg3L4d2', 'plJovUlxfQ0', 'CHp6Bd9CR0Q'];

    for (const de of parentDataElements) {
      const parentId = objective.dataValues?.find(dv => dv.dataElement === de)?.value;
      if (parentId) {
        // Get the parent objective
        const parentObjective = allObjectivesById[parentId];

        // Try to get the goal name from the parent objective
        let parentGoalName = null;
        let isTopLevelAsGoal = false;

        // FIRST: Check if parent itself has a direct goal link
        if (parentObjective) {
          const parentGoalDataValue = parentObjective.dataValues?.find(
            dv => dv.dataElement === 'VhrIQQIVpB9'
          );

          if (parentGoalDataValue?.value) {
            parentGoalName = goalNameById[parentGoalDataValue.value];
            console.log('🎯 Parent has direct goal link:', {
              parentId,
              goalId: parentGoalDataValue.value,
              goalName: parentGoalName
            });
          }

          // SECOND: If parent doesn't have goal, check if parent is actually a GOAL
          if (!parentGoalName) {
            // Check if parentId matches any goal ID
            if (goalNameById[parentId]) {
              parentGoalName = goalNameById[parentId];
              console.log('🎯 Parent IS a goal:', {
                parentId,
                goalName: parentGoalName
              });
            }
          }

          // THIRD: If still no goal, check if parent is a TOP-LEVEL OBJECTIVE (no parent itself)
          if (!parentGoalName && parentObjective) {
            // Check if parent objective has NO parent (making it top-level)
            const parentHasParent = parentDataElements.some(de =>
              parentObjective.dataValues?.some(dv => dv.dataElement === de)
            );

            if (!parentHasParent) {
              // This parent is a top-level objective - treat it as a goal!
              parentGoalName = parentObjective.name;
              isTopLevelAsGoal = true;
              console.log('🎯 Parent is TOP-LEVEL OBJECTIVE (treating as goal):', {
                parentId,
                parentName: parentGoalName,
                isTopLevel: true
              });
            }
          }

          // FOURTH: If still no goal, check parent's parent (recursive)
          if (!parentGoalName) {
            const grandParentInfo = getLinkedGoalInfo(parentObjective, goalItems, goalNameById, allObjectivesById);
            if (grandParentInfo.goalName && grandParentInfo.goalName !== 'No Goal Linked') {
              parentGoalName = grandParentInfo.goalName;
              isTopLevelAsGoal = grandParentInfo.isTopLevelObjectiveAsGoal;
              console.log('🎯 Found goal in grandparent:', {
                parentId,
                goalName: parentGoalName,
                fromGrandparent: true
              });
            }
          }
        }

        console.log('🔗 [FOUND PARENT OBJECTIVE LINK]', {
          objectiveId: objective.id,
          parentId: parentId,
          parentName: parentObjective?.name || 'Unknown',
          parentHasGoalLink: !!parentObjective?.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9'),
          parentIsGoal: !!goalNameById[parentId],
          parentIsTopLevelObjective: isTopLevelAsGoal,
          parentGoalName: parentGoalName || 'No goal linked'
        });

        return {
          linkedId: parentId,
          linkedName: parentObjective?.name || `Objective ${parentId}`,
          goalName: parentGoalName || 'No Goal Linked',
          isCompanyGoal: false,
          isParentObjective: true,
          isTopLevelObjectiveAsGoal: isTopLevelAsGoal,
          parentObjective: parentObjective,
          parentIsGoal: !!goalNameById[parentId]
        };
      }
    }

    console.log('❌ No links found for objective');
    return {
      linkedId: null,
      linkedName: null,
      goalName: null,
      isCompanyGoal: false,
      isParentObjective: false,
      isTopLevelObjectiveAsGoal: false
    };
  };

  // Wrap all state updates with this check
  const safeSetState = (setterFunction, ...args) => {
    if (isMounted.current) {
      setterFunction(...args);
    }
  };

  const normalizedLoggedInUser = user?.username?.toLowerCase().trim() || '';
  //const isTopLevelUser = !reportingToPosition;

  const attributes = currentUserPosition ? extractAttributes(currentUserPosition.attributes) : {};
  const hasReportingTo = !!attributes[REPORTING_TO];
  const isTopLevelUser = !hasReportingTo;

  const filterObjectives = (
      objectives,
      searchTerm,
      {
        section = 'my', // 'my' or 'team'
        goalNameById = {},
        staffList = [],
        getPositionStaff = () => null,
        extractAttributes = () => ({}),
        POSITION_ATTRIBUTES = {}
      } = {}
    ) => {
      return objectives.filter((obj) => {
        // First apply goal filters if any are selected
        if (selectedGoalFilters.length > 0) {
          const objectiveGoalId = getObjectiveGoalId(obj);
          if (!objectiveGoalId || !selectedGoalFilters.includes(objectiveGoalId)) {
            return false;
          }
        }

        // Then apply search filter
        if (!searchTerm.trim()) return true;
        
        const term = searchTerm.toLowerCase().trim();

        // Always check name and status
        const matchesName = (obj.name || '').toLowerCase().includes(term);
        const matchesStatus = (obj.approvalStatus || '').toLowerCase().includes(term);

        // My Objectives: check dates & goal
        if (section === 'my') {
          const matchesStart = (obj.startDate || '').includes(term);
          const matchesCompletion = (obj.completionDate || '').includes(term);
          const linkedGoalId = getObjectiveGoalId(obj);
          const linkedGoalName = (goalNameById[linkedGoalId] || '').toLowerCase();
          const matchesGoal = linkedGoalName.includes(term);

          return matchesName || matchesStatus || matchesStart || matchesCompletion || matchesGoal;
        }

        // Direct Reports: check staff, position, goal
        if (section === 'team') {
          const staff = getPositionStaff(obj.position, staffList);
          const staffFullName = `${staff?.firstName || ''} ${staff?.surname || ''}`.toLowerCase();
          const matchesStaff = staffFullName.includes(term);

          const positionAttrs = extractAttributes(obj.position?.attributes || []);
          const positionTitle = (positionAttrs[POSITION_ATTRIBUTES.POSITION_TITLE] || '').toLowerCase();
          const matchesPosition = positionTitle.includes(term);

          const linkedGoalId = getObjectiveGoalId(obj);
          const linkedGoalName = (goalNameById[linkedGoalId] || '').toLowerCase();
          const matchesGoal = linkedGoalName.includes(term);

          return matchesName || matchesStatus || matchesStaff || matchesPosition || matchesGoal;
        }

        return matchesName || matchesStatus;
      });
  };

  
  // function to build the reporting chain
  const getReportingChain = async (startPositionTei) => {
    const chain = [];
    let currentPositionTei = startPositionTei;
    const visited = new Set(); // Prevent infinite loops

    while (currentPositionTei && !visited.has(currentPositionTei)) {
      visited.add(currentPositionTei);

      try {
        // Get position details including reportingTo attribute
        const positionResponse = await engine.query({
          position: {
            resource: 'trackedEntityInstances',
            id: currentPositionTei,
            params: {
              fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            },
          },
        });

        const position = positionResponse.position;
        chain.push({
          tei: currentPositionTei,
          position: position,
          attributes: extractAttributes(position.attributes || [])
        });

        // Get the reportingTo attribute value (next level up)
        const reportingToAttr = position.attributes?.find(
          attr => attr.attribute === REPORTING_TO
        )?.value;

        if (!reportingToAttr || reportingToAttr === currentPositionTei) {
          break; // Reached top or loop detected
        }

        currentPositionTei = reportingToAttr;
      } catch (error) {
        console.error(`Error getting position ${currentPositionTei}:`, error);
        break;
      }
    }

    return chain;
  };

  const loadGoalLinkages = async () => {
    console.log('🔗 [LOADING GOAL LINKAGES]');

    try {
      // Get all objectives
      const allObjectiveIds = [
        ...myObjectives,
        ...reportingToObjectives,
        ...directReportObjectives
      ].map(obj => obj.id);

      if (allObjectiveIds.length === 0) {
        console.log('No objectives to check for linkages');
        return {};
      }

      const response = await engine.query({
        linkages: {
          resource: 'events',
          params: {
            program: 'NkReudUAW4F', // Company Goals Management Program
            programStage: 'IEsUtu84bQU', // Objective-Goal Linkages stage
            ouMode: 'ACCESSIBLE',
            fields: 'event,dataValues[dataElement,value]',
            filter: allObjectiveIds.map(id => `plJovUlxfQ0:eq:${id}`).join(';'),
            pageSize: 1000,
          },
        },
      });

      const linkages = response.linkages?.events || [];

      // Create a mapping: objectiveId -> goalId
      const linkageMap = {};
      linkages.forEach(linkage => {
        const objectiveId = linkage.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
        const goalId = linkage.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;

        if (objectiveId && goalId) {
          linkageMap[objectiveId] = goalId;
        }
      });

      console.log('🔗 [GOAL LINKAGE MAP]', {
        totalLinkages: linkages.length,
        uniqueObjectives: Object.keys(linkageMap).length,
        sample: Object.keys(linkageMap).slice(0, 3).map(objId => ({
          objectiveId: objId,
          goalId: linkageMap[objId],
          goalName: goalNameById[linkageMap[objId]]
        }))
      });

      return linkageMap;
    } catch (error) {
      console.error('Error loading goal linkages:', error);
      return {};
    }
  };

  const grantFullOrgUnitAccess = async (userId, orgUnitId) => {
    try {
      // Get the org unit details including path
      const orgUnitResponse = await engine.query({
        orgUnit: {
          resource: 'organisationUnits',
          id: orgUnitId,
          params: {
            fields: 'id,path,level,ancestors[id]'
          }
        }
      });

      const orgUnit = orgUnitResponse.orgUnit;
      const ancestors = orgUnit.ancestors || [];

      // Get all ancestor IDs
      const ancestorIds = ancestors.map(a => a.id);

      console.log('🔑 [GRANTING FULL ACCESS] Org unit tree:', {
        targetOrgUnit: orgUnitId,
        ancestors: ancestorIds,
        path: orgUnit.path
      });

      // Grant access to all ancestors + the target org unit
      const allOrgUnitsToGrant = [...ancestorIds, orgUnitId];

      // Get current user access
      const userResponse = await engine.query({
        user: {
          resource: 'users',
          id: userId,
          params: {
            fields: 'organisationUnits[id],dataViewOrganisationUnits[id]'
          }
        }
      });

      const currentUser = userResponse.user;
      const currentOrgUnits = currentUser.organisationUnits.map(ou => ou.id);
      const currentDataViewUnits = currentUser.dataViewOrganisationUnits.map(ou => ou.id);

      const newOrgUnits = [...new Set([...currentOrgUnits, ...allOrgUnitsToGrant])];
      const newDataViewUnits = [...new Set([...currentDataViewUnits, ...allOrgUnitsToGrant])];

      // Update user
      const updateResponse = await engine.mutate({
        resource: `users/${userId}`,
        type: 'update',
        data: {
          organisationUnits: newOrgUnits.map(id => ({ id })),
          dataViewOrganisationUnits: newDataViewUnits.map(id => ({ id }))
        }
      });

      console.log('✅ [FULL ACCESS GRANTED] to org unit tree');
      return true;
    } catch (error) {
      console.error('❌ Error granting full org unit access:', error);
      return false;
    }
  };

  // Replace your verifyObjectiveFiltering function with this:
  const verifyObjectiveFiltering = (currentUserPosition, directReportPositions, events) => {
    console.log('=== OBJECTIVE FILTERING VERIFICATION ===');

    console.log('📊 Current User Position TEI:', currentUserPosition.trackedEntityInstance);

    // Check current user's objectives using data element SlgO60cMK3F
    const currentUserObjs = events.filter(event =>
      event.programStage === 'VIBtpvkpHFR' &&
      event.dataValues?.some(dv =>
        dv.dataElement === 'SlgO60cMK3F' && dv.value === currentUserPosition.trackedEntityInstance
      )
    );
    console.log(`📊 Current User Objectives: ${currentUserObjs.length}`);

    // Check direct report objectives
    directReportPositions.forEach(position => {
      const positionTei = position.trackedEntityInstance;
      const positionTitle = position.attributes?.find(attr =>
        attr.attribute === POSITION_ATTRIBUTES.POSITION_TITLE
      )?.value || 'Unknown';

      const directReportObjs = events.filter(event =>
        event.programStage === 'VIBtpvkpHFR' &&
        event.dataValues?.some(dv =>
          dv.dataElement === 'SlgO60cMK3F' && dv.value === positionTei
        )
      );

      console.log(`📊 "${positionTitle}" (${positionTei}): ${directReportObjs.length} objectives`);
    });

    // COMPREHENSIVE ANALYSIS
    console.log('🔍 [COMPREHENSIVE POSITION ANALYSIS]');
    console.log('Current User Position TEI:', currentUserPosition.trackedEntityInstance);

    const directReportPositionTeis = directReportPositions.map(pos => pos.trackedEntityInstance);
    console.log('Direct Report Position TEIs:', directReportPositionTeis);

    // Analyze each event
    events.forEach((event, index) => {
      const positionDataValue = event.dataValues?.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;
      console.log(`Event ${index}: ${event.event} - SlgO60cMK3F value: ${positionDataValue}`);

      // Check if this matches any direct report positions
      const isDirectReportObj = directReportPositionTeis.includes(positionDataValue);
      if (isDirectReportObj) {
        console.log(`✅ This objective belongs to direct report: ${positionDataValue}`);
      }
    });

    // IMMEDIATE TEST
    console.log('🔍 [DIRECT REPORT TEST]');
    const directReportObjs = events.filter(event =>
      event.programStage === 'VIBtpvkpHFR' &&
      event.dataValues?.some(dv =>
        dv.dataElement === 'SlgO60cMK3F' && directReportPositionTeis.includes(dv.value)
      )
    );
    console.log('Found direct report objectives:', directReportObjs.length);
    directReportObjs.forEach(obj => {
      const positionValue = obj.dataValues?.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;
      console.log(`   - ${obj.event} for position: ${positionValue}`);
    });
  };

  const getChildCount = (objectiveId) => {
    return childCounts[objectiveId] || 0;
  };

  // Goal filter handlers
  const handleGoalFilterChange = (goalId) => {
      setSelectedGoalFilters(prev => {
        if (prev.includes(goalId)) {
          return prev.filter(id => id !== goalId);
        } else {
          return [...prev, goalId];
        }
      });
    };

    const clearAllGoalFilters = () => {
      setSelectedGoalFilters([]);
    };

    const toggleFilterPopover = () => {
      setIsFilterPopoverOpen(!isFilterPopoverOpen);
  };

  useEffect(() => {
    console.log('🔄 reportingToPosition STATE UPDATED:', {
      tei: reportingToPosition?.trackedEntityInstance,
      orgUnit: reportingToPosition?.orgUnit,
      hasOrgUnit: !!reportingToPosition?.orgUnit,
      fullObject: reportingToPosition
    });
  }, [reportingToPosition]);

  useEffect(() => {
    const calculateProtectedObjectives = async () => {
      const protectedSet = new Set();
      const counts = {};

      // Check only MY objectives for child relationships
      const checkPromises = myObjectives.map(async (obj) => {
        try {
          const childInfo = await getChildObjectivesInfo(obj.id, engine);

          if (childInfo.length > 0) {
            protectedSet.add(obj.id);
            counts[obj.id] = childInfo.length;
          }
          return { id: obj.id, hasChildren: childInfo.length > 0 };
        } catch (error) {
          console.error(`Error checking objective ${obj.id}:`, error);
          protectedSet.add(obj.id); // Protect on error
          return { id: obj.id, hasChildren: true };
        }
      });

      await Promise.all(checkPromises);
      setProtectedObjectives(protectedSet);
      setChildCounts(counts);
    };

    if (myObjectives.length > 0) {
      calculateProtectedObjectives();
    }
  }, [myObjectives, engine]);

  useEffect(() => {
  let isMounted = true;

  const loadObjectivesByPosition = async () => {
    // ✅ Check basic prerequisites
    if (
      !reportingStructureLoaded ||
      !staffList?.length ||
      !currentUserPosition?.trackedEntityInstance ||
      !user?.id ||
      !fiscalYear?.start ||
      !fiscalYear?.end
    ) {
      console.log('⚠️ [DEBUG] Skipping objective processing - missing data', {
        reportingStructureLoaded,
        staffListLength: staffList?.length || 0,
        currentUserPosition: !!currentUserPosition?.trackedEntityInstance,
        userId: user?.id,
        fiscalYear: fiscalYear?.start ? 'ready' : 'missing'
      });
      return;
    }

    // ✅ FIXED: Handle top-level users properly
    const isTopLevel = !reportingToPosition?.trackedEntityInstance;

    // If not top-level, we need reportingToPosition
    if (!isTopLevel && !reportingToPosition?.trackedEntityInstance && directReportPositions?.length === 0) {
      console.log('⏳ Waiting for reporting structure data...');
      return;
    }

    // For top-level users, we only need currentUserPosition
    if (isTopLevel && !currentUserPosition?.trackedEntityInstance) {
      console.log('⏳ Waiting for current user position...');
      return;
    }

    console.log(`🎯 [LOADING OBJECTIVES BY POSITION TEI]`);
    console.log(`📊 User type: ${isTopLevel ? 'Top-Level (CEO/Director)' : 'Regular User'}`);
    console.log('📊 Data available:', {
      currentPosition: currentUserPosition.trackedEntityInstance,
      reportingTo: reportingToPosition?.trackedEntityInstance || 'none (top level)',
      directReports: directReportPositions?.length || 0,
      staffCount: staffList.length
    });

    try {
      // ✅ STEP 1: Grant full access to manager's org unit tree (only if not top-level)
      if (reportingToPosition?.orgUnit && !isTopLevel) {
        console.log('🔑 Granting full access to manager org unit:', reportingToPosition.orgUnit);
        await grantFullOrgUnitAccess(user.id, reportingToPosition.orgUnit);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // ✅ STEP 2: Collect position TEIs
      const positionTeisToQuery = new Set();
      positionTeisToQuery.add(currentUserPosition.trackedEntityInstance);
      
      if (reportingToPosition?.trackedEntityInstance) {
        positionTeisToQuery.add(reportingToPosition.trackedEntityInstance);
      }
      
      directReportPositions.forEach(pos => {
        if (pos.trackedEntityInstance) {
          positionTeisToQuery.add(pos.trackedEntityInstance);
        }
      });

      // ✅ STEP 3: Get root org unit
      let rootOrgUnit = 'YbzM5Sy37YS';
      if (user?.organisationUnits?.length > 0) {
        try {
          const orgUnitResponse = await engine.query({
            orgUnit: {
              resource: 'organisationUnits',
              id: user.organisationUnits[0].id,
              params: { fields: 'id,path' }
            }
          });
          const path = orgUnitResponse.orgUnit?.path || '';
          const pathParts = path.split('/');
          if (pathParts.length > 1) {
            rootOrgUnit = pathParts[1];
            console.log('🔍 Found root org unit from path:', rootOrgUnit);
          }
        } catch (error) {
          console.warn('⚠️ Could not determine root org unit, using default');
        }
      }

      console.log('🔍 [FETCHING OBJECTIVES] Using ROOT org unit:', rootOrgUnit);

      // ✅ STEP 4: Query from root org unit - Use ACCESSIBLE mode instead of ALL
      let response;
      try {
        response = await engine.query({
          allObjectives: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage: 'VIBtpvkpHFR',
              orgUnit: rootOrgUnit,
              ouMode: 'ACCESSIBLE',  // ✅ Changed from 'ALL' to 'ACCESSIBLE'
              fields: 'event,orgUnit,trackedEntityInstance,eventDate,createdAt,dataValues[dataElement,value]',
              pageSize: 1000,
            },
          },
        });
      } catch (queryError) {
        console.error('❌ [FETCHING OBJECTIVES] Query failed:', queryError);
        if (isMounted) {
          setMyObjectives([]);
          setReportingToObjectives([]);
          setDirectReportObjectives([]);
        }
        return;
      }

      const allEvents = response.allObjectives?.events || [];
      console.log('📊 [ALL OBJECTIVES FOUND - ObjectiveList]', {
        count: allEvents.length,
        sample: allEvents.slice(0, 3).map(e => ({
          event: e.event,
          positionRef: e.dataValues?.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value,
          startDate: e.dataValues?.find(dv => dv.dataElement === 'eDcVy661j9e')?.value,
          completionDate: e.dataValues?.find(dv => dv.dataElement === 'J0rjxrDKn1l')?.value
        }))
      });

      // ✅ STEP 5: Filter by position TEI
      const myObjs = [];
      const reportingToObjs = [];
      const directReportObjs = [];

      allEvents.forEach(event => {
        const positionTei = event.dataValues?.find(dv =>
          dv.dataElement === 'SlgO60cMK3F'
        )?.value;

        if (!positionTei) return;

        if (positionTei === currentUserPosition.trackedEntityInstance) {
          myObjs.push(event);
        }
        else if (positionTei === reportingToPosition?.trackedEntityInstance) {
          reportingToObjs.push(event);
        }
        else if (directReportPositions.some(pos =>
          pos.trackedEntityInstance === positionTei
        )) {
          directReportObjs.push(event);
        }
      });

      console.log('✅ [OBJECTIVE ASSIGNMENT BY POSITION]', {
        myObjectives: myObjs.length,
        managerObjectives: reportingToObjs.length,
        teamObjectives: directReportObjs.length,
        unmatchedEvents: allEvents.length - (myObjs.length + reportingToObjs.length + directReportObjs.length)
      });

      // ✅ STEP 6: Map to objects
      const mapEventsToObjects = (events, staffList) => {
        return events.map(event => {
          const dataValues = event.dataValues || [];
          const category = dataValues.find(dv => dv.dataElement === 'uBPW95V2VoX')?.value || 'Not set';
          const staffId = dataValues.find(dv => dv.dataElement === 'V66IFsw5kq6')?.value;
          const staff = staffList.find(s => s.id === staffId);
          const positionTei = dataValues.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;
          const position = [
            currentUserPosition,
            reportingToPosition,
            ...directReportPositions
          ].find(pos => pos?.trackedEntityInstance === positionTei);

          return {
            id: event.event,
            name: dataValues.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed',
            description: dataValues.find(dv => dv.dataElement === 'nWqW5BrcgP1')?.value || '',
            orgUnit: event.orgUnit,
            startDate: dataValues.find(dv => dv.dataElement === 'eDcVy661j9e')?.value || '',
            completionDate: dataValues.find(dv => dv.dataElement === 'J0rjxrDKn1l')?.value || '',
            status: dataValues.find(dv => dv.dataElement === 'DL7KxNhiV0x')?.value || '',
            approvalStatus: dataValues.find(dv => dv.dataElement === 'DzQorireRBf')?.value || 'pending',
            managerPrivateNote: dataValues.find(dv => dv.dataElement === 'rJZyovd5akX')?.value || '',
            noteCreatedBy: dataValues.find(dv => dv.dataElement === 'x56k8a6rLss')?.value || '',
            noteCreatedAt: dataValues.find(dv => dv.dataElement === 'TlhIr80b8Vp')?.value || '',
            dataValues: dataValues,
            position: position,
            linkedStaff: staff,
            category: category,
          };
        }).filter(obj => {
          const fyReady = fiscalYear?.start && fiscalYear?.end && fiscalYear.start !== '' && fiscalYear.end !== '';
          if (!fyReady) {
            console.log('🔍 [OBJECTIVE FILTER] Skipping - fiscalYear not ready:', fiscalYear);
            return true;
          }
           
          if (!fiscalYear?.start || !fiscalYear?.end) return true;

            const inStart = obj.startDate       ? isWithinFiscalYear(obj.startDate, fiscalYear)       : false;
            const inEnd   = obj.completionDate  ? isWithinFiscalYear(obj.completionDate, fiscalYear)  : false;

            // Keep if EITHER date falls in FY, OR if the objective has no dates at all
            const keep = inStart || inEnd || (!obj.startDate && !obj.completionDate);

            if (!keep) {
              console.log('🗑️ [OBJ FILTER] Dropped', {
                name: obj.name,
                startDate: obj.startDate,
                completionDate: obj.completionDate,
                fiscalYear,
              });
            }
            return keep;

        });
      };

      if (isMounted) {
        const mappedMyObjectives = mapEventsToObjects(myObjs, staffList);
        const mappedReportingTo = mapEventsToObjects(reportingToObjs, staffList);
        const mappedDirectReports = mapEventsToObjects(directReportObjs, staffList);
        
        // ✅ Set all states together
        setMyObjectives(mappedMyObjectives);
        setReportingToObjectives(mappedReportingTo);
        setDirectReportObjectives(mappedDirectReports);
        setManagerObjectivesLoaded(true);
        
        // ✅ Set loaded flag AFTER all states are set
        setObjectivesLoaded(true);
        
        console.log('✅ [OBJECTIVES LOADED BY REPORTING STRUCTURE]', {
          myCount: mappedMyObjectives.length,
          managerCount: mappedReportingTo.length,
          teamCount: mappedDirectReports.length
        });
      }

    } catch (error) {
      console.error('❌ [ERROR LOADING OBJECTIVES BY POSITION]:', error);
      if (isMounted) {
        setMyObjectives([]);
        setReportingToObjectives([]);
        setDirectReportObjectives([]);
        setObjectivesLoaded(true); // Still mark as loaded to prevent infinite loading
      }
    }
  };

  loadObjectivesByPosition();

  return () => {
    isMounted = false;
  };
}, [
  reportingStructureLoaded,
  currentUserPosition?.trackedEntityInstance,
  reportingToPosition?.trackedEntityInstance,
  directReportPositions?.length,
  staffList?.length,
  user?.id,
  fiscalYear?.start,
  fiscalYear?.end, 
]);

  const [objectivesLoadingComplete, setObjectivesLoadingComplete] = useState(false);

  useEffect(() => {
    // Only mark as complete after the objectives have actually been loaded
    if (objectivesLoaded) {
      setObjectivesLoadingComplete(true);
    }
  }, [objectivesLoaded]);

  // Calculate loading state
  const isLoading = loadingEvents || 
                  !reportingStructureLoaded || 
                  staffList.length === 0 || 
                  !objectivesLoaded;

  // Calculate total visible objectives
  const totalVisible = myObjectives.length + reportingToObjectives.length + directReportObjectives.length;

  // Calculate empty state
  const shouldShowEmptyState = useMemo(() => {
    if (isLoading) return false;
    return objectivesLoadingComplete && totalVisible === 0;
  }, [isLoading, objectivesLoadingComplete, totalVisible]);

  const comprehensiveDataInspection = async (userId, orgUnits, engine) => {
    console.log('🔍 [COMPREHENSIVE DATA INSPECTION] Starting for user:', userId);

    const orgUnitIds = orgUnits.map(ou => ou.id).join(';');
    console.log('🔍 Org Units being queried:', orgUnitIds);

    try {
      // 1. Check ALL tracked entity instances in the org unit (not just positions)
      const allTEIs = await engine.query({
        allEntities: {
          resource: 'trackedEntityInstances',
          params: {
            ou: orgUnitIds,
            ouMode: 'ACCESSIBLE',
            fields: 'trackedEntityInstance,trackedEntityType,orgUnit,attributes[attribute,value]',
            pageSize: 50
          }
        }
      });

      console.log('🔍 ALL Tracked Entity Instances in org unit:', {
        total: allTEIs.allEntities?.trackedEntityInstances?.length || 0,
        byType: allTEIs.allEntities?.trackedEntityInstances?.reduce((acc, tei) => {
          acc[tei.trackedEntityType] = (acc[tei.trackedEntityType] || 0) + 1;
          return acc;
        }, {})
      });

      // 2. Check specifically for Position TEIs with different queries
      const positionQueries = {
        byTrackedEntityType: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            ou: orgUnitIds,
            ouMode: 'ACCESSIBLE',
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            pageSize: 50
          }
        },
        byProgram: {
          resource: 'trackedEntityInstances',
          params: {
            program: 'yiT00XKrrHP', // Your performance program
            ou: orgUnitIds,
            ouMode: 'ACCESSIBLE',
            fields: 'trackedEntityInstance,trackedEntityType,orgUnit,attributes[attribute,value]',
            pageSize: 50
          }
        }
      };

      const [byTypeResult, byProgramResult] = await Promise.all([
        engine.query({ positionsByType: positionQueries.byTrackedEntityType }),
        engine.query({ positionsByProgram: positionQueries.byProgram })
      ]);

      console.log('🔍 POSITION SEARCH RESULTS:', {
        byTrackedEntityType: byTypeResult.positionsByType?.trackedEntityInstances?.length || 0,
        byProgram: byProgramResult.positionsByProgram?.trackedEntityInstances?.length || 0
      });

      // 3. Check what objectives exist in the org unit (regardless of linkage)
      const objectives = await engine.query({
        objectives: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: 'VIBtpvkpHFR',
            orgUnit: orgUnitIds,
            fields: 'event,orgUnit,trackedEntityInstance,dataValues[dataElement,value]',
            pageSize: 50
          }
        }
      });

      console.log('🔍 OBJECTIVES IN ORG UNIT:', {
        total: objectives.objectives?.events?.length || 0,
        sample: objectives.objectives?.events?.slice(0, 3).map(event => ({
          event: event.event,
          tei: event.trackedEntityInstance,
          dataValues: event.dataValues?.map(dv => ({
            dataElement: dv.dataElement,
            value: dv.value
          }))
        }))
      });

      // 4. Check user details to understand their access
      console.log('🔍 USER DETAILS:', {
        id: userId,
        orgUnits: orgUnits.map(ou => ({ id: ou.id, name: ou.name })),
      });

      return {
        allTEIs: allTEIs.allEntities?.trackedEntityInstances || [],
        positionsByType: byTypeResult.positionsByType?.trackedEntityInstances || [],
        positionsByProgram: byProgramResult.positionsByProgram?.trackedEntityInstances || [],
        objectives: objectives.objectives?.events || []
      };

    } catch (error) {
      console.error('❌ Comprehensive inspection error:', error);
      return { allTEIs: [], positionsByType: [], positionsByProgram: [], objectives: [] };
    }
  };


  // Placeholder drag handlers
  const handleDragFromManager = (objective) => {
    console.log('Drag from manager:', objective);
    alert('Drag-from-manager: Create aligned objective from this.');
  };

  // ✅ Helper: Check if objective owner is a direct report
  const isMyDirectReport = (objective) => {
    if (!objective?.position || !directReportPositions.length) return false;
    return directReportPositions.some((pos) =>
      pos.trackedEntityInstance === objective.position.trackedEntityInstance
    );
  };



  // ------------------ Event Handlers ------------------
  const toggleExpand = (objectiveId) => {
    setExpandedObjectiveId((prevId) => (prevId === objectiveId ? null : objectiveId));
  };

  const checkForChildObjectives = async (objectiveId, engine) => {
    try {
      console.log('🔍 [CHILD CHECK] Checking for child objectives linked to:', objectiveId);

      // Remove the "broader check" that uses *:like: filter
      const response = await engine.query({
        childObjectives: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: 'VIBtpvkpHFR',
            fields: 'event,trackedEntityInstance,orgUnit,dataValues[dataElement,value]',
            filter: `plJovUlxfQ0:eq:${objectiveId}`,
            pageSize: 50,
          },
        },
      });

      const childObjectives = response.childObjectives?.events || [];

      console.log('🔍 [CHILD CHECK RESULTS]', {
        parentId: objectiveId,
        childCount: childObjectives.length,
        children: childObjectives.map(obj => ({
          id: obj.event,
          name: obj.dataValues?.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed',
          // Check if this child has the parent link
          parentRef: obj.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value
        }))
      });

      return childObjectives.length > 0;
    } catch (error) {
      console.error('❌ Error checking for child objectives:', error);
      // Return true to be safe - don't allow deletion if we can't verify
      return true;
    }
  };


  const getChildObjectivesInfo = async (objectiveId, engine) => {
    try {
      console.log('🔍 [GET CHILD INFO] For objective:', objectiveId);

      const PARENT_REF_DATA_ELEMENT = 'neuilg3L4d2';

      const response = await engine.query({
        childObjectives: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: 'VIBtpvkpHFR',
            fields: 'event,orgUnit,eventDate,dataValues[dataElement,value],trackedEntityInstance',
            filter: `${PARENT_REF_DATA_ELEMENT}:eq:${objectiveId}`,
            pageSize: 50,
          },
        },
      });

      const childEvents = response.childObjectives?.events || [];

      console.log('✅ [CHILD CHECK]', {
        parentId: objectiveId,
        childCount: childEvents.length,
        usingDataElement: PARENT_REF_DATA_ELEMENT,
        children: childEvents.map(e => ({
          id: e.event,
          name: e.dataValues?.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed'
        }))
      });

      // Process child info
      const childInfo = childEvents.map(event => {
        const dataValues = {};
        event.dataValues?.forEach(dv => {
          dataValues[dv.dataElement] = dv.value;
        });

        return {
          id: event.event,
          name: dataValues['EuN5ztFnXsQ'] || 'Unnamed Objective',
          createdBy: dataValues['Uhi5GhGQieE'] || 'Unknown',
          positionTei: dataValues['SlgO60cMK3F'],
          createdDate: event.eventDate,
        };
      });

      return childInfo;
    } catch (error) {
      console.error('❌ Error getting child objectives info:', error);
      return [];
    }
  };


  const checkAllObjectiveRelationships = async () => {
    console.log('🔗 [RELATIONSHIP CHECK] Starting comprehensive check...');

    const allObjectives = [
      ...myObjectives,
      ...directReportObjectives,
      ...reportingToObjectives
    ];

    if (allObjectives.length === 0) {
      console.log('No objectives to check');
      return;
    }

    // First, get ALL events to see what's actually stored
    const orgUnitIds = user?.organisationUnits?.map(ou => ou.id).join(';') || '';

    try {
      const response = await engine.query({
        allEvents: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: 'VIBtpvkpHFR',
            orgUnit: orgUnitIds,
            ouMode: 'ACCESSIBLE',
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 1000,
          },
        },
      });

      const allEvents = response.allEvents?.events || [];
      console.log(`📊 Total events in system: ${allEvents.length}`);

      // Check each event for parent references
      const relationships = [];

      allEvents.forEach(event => {
        const dataValues = event.dataValues || [];
        const parentRef = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0');

        if (parentRef) {
          const childName = dataValues.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed';
          const parentName = allObjectives.find(obj => obj.id === parentRef.value)?.name || 'Unknown Parent';

          relationships.push({
            childEvent: event.event,
            childName: childName,
            parentEvent: parentRef.value,
            parentName: parentName,
          });
        }
      });


      return relationships;
    } catch (error) {
      console.error('Relationship check error:', error);
      return [];
    }
  };

  const handleDelete = async (orgUnit, objectiveId) => {
    // Final safety check - should never trigger since buttons are hidden, but good to have
    const hasChildren = await checkForChildObjectives(objectiveId, engine);
    if (hasChildren) {
      // Get detailed information
      const childInfo = await getChildObjectivesInfo(objectiveId, engine);

      alert(
        `🚫 Deletion Blocked\n\n` +
        `This objective cannot be deleted because ${childInfo.length} ` +
        `subordinate objective(s) are linked to it:\n\n` +
        childInfo.map((child, index) =>
          `${index + 1}. "${child.name}" (Created by: ${child.createdBy})`
        ).join('\n') +
        `\n\nPlease contact the subordinate users to unlink or delete their objectives first.`
      );
      return;
    }

    if (!window.confirm('Delete this objective? This cannot be undone.')) return;

    try {
      const authHeader = getAuthHeader();
      const result = await engine.mutate({
        resource: `events/${objectiveId}`,
        type: 'delete',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
      });

      if (['OK', 200].includes(result?.httpStatus || result?.status)) {
        alert('Deleted successfully!');
        window.location.reload();
      } else {
        alert('Failed to delete.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting objective.');
    }
  };


  const canDeleteObjective = (objective) => {
    // DEBUG: Log what we're checking
    console.log('🔍 [CAN DELETE CHECK - DETAILED]', {
      objectiveId: objective.id,
      objectiveName: objective.name,
      isInProtectedSet: protectedObjectives.has(objective.id),
      protectedSetSize: protectedObjectives.size,
      isMyObjectiveCheck: myObjectives.some(obj => obj.id === objective.id),
      currentUserPositionTEI: currentUserPosition?.trackedEntityInstance,
      objectivePositionTEI: objective.position?.trackedEntityInstance,
      isTopLevelUser
    });


    if (protectedObjectives.has(objective.id)) {
      return {
        canDelete: false,
        reason: 'Has linked aligned objectives',
        type: 'protected'
      };
    }

    // ✅ FIX: Check ownership
    const isMyObjective = currentUserPosition?.trackedEntityInstance ===
      objective.position?.trackedEntityInstance;

    console.log('🔍 [OWNERSHIP CHECK]', {
      userPositionTEI: currentUserPosition?.trackedEntityInstance,
      objectivePositionTEI: objective.position?.trackedEntityInstance,
      isMyObjective
    });

    // If not my objective, can't delete it
    if (!isMyObjective) {
      console.log('🚫 Not user\'s objective:', objective.id);
      return { canDelete: false, reason: 'Not Your Objective', type: 'ownership' };
    }

    // ✅ IMPORTANT CHANGE: For NON-top-level users, CAN delete if status is 'pending' or 'returned'
    if (!isTopLevelUser) {
      if (objective.approvalStatus === 'approved') {
        console.log('🚫 Approved objective for non-top-level user:', objective.id);
        return { canDelete: false, reason: 'Approved objectives cannot be deleted', type: 'approval' };
      }
      // Allow deletion if status is 'pending' or 'returned'
      console.log('✅ Can delete (non-top-level, not approved):', objective.id);
      return { canDelete: true, reason: '', type: '' };
    }

    // Top-level users can always delete their objectives
    console.log('✅ Can delete objective (top-level user):', objective.id);
    return { canDelete: true, reason: '', type: '' };
  };


  const handleApproval = async (objective, status) => {
    const remark = window.prompt(`Enter remarks for ${status}:`, objective.approvalRemark || '');
    if (remark === null) return;

    try {
      setLoading(true);
      const filteredDataValues = (objective.dataValues || []).filter(
        (dv) => !['DzQorireRBf', 'KeGWxm1IDYF'].includes(dv.dataElement)
      );
      const updatedDataValues = [
        ...filteredDataValues,
        { dataElement: 'DzQorireRBf', value: status },
        { dataElement: 'KeGWxm1IDYF', value: remark },
      ];

      const eventData = {
        event: objective.id,
        program: 'yiT00XKrrHP',
        programStage: 'VIBtpvkpHFR',
        orgUnit: objective.orgUnit,
        eventDate: objective.startDate || new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        dataValues: updatedDataValues,
      };

      const authHeader = getAuthHeader();
      await engine.mutate({
        resource: `events/${objective.id}`,
        type: 'update',
        data: eventData,
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
      });

      // Update local context
      setData((oldData) =>
        oldData.map((level) => {
          if (level.level !== editingLevelId) return level;
          return {
            ...level,
            objectives: level.objectives.map((obj) =>
              obj.id === objective.id
                ? { ...obj, approvalStatus: status, approvalRemark: remark }
                : obj
            ),
          };
        })
      );

      if (status === 'approved' && reportingToPosition) {
            try {
                await fetch(`${process.env.REACT_APP_API_URL}/api/email/manager-objectives-approved`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        managerTei: reportingToPosition.trackedEntityInstance,
                        fiscalYearStart: fiscalYear.start,
                        fiscalYearEnd: fiscalYear.end
                    })
                });
                // Ignore response; backend handles duplicates
            } catch (err) {
                console.error('Error sending manager approval notification:', err);
            }
        }

      alert(`Objective ${status} successfully!`);

    } catch (error) {
      console.error('Approval error:', error);
      alert('Failed to update approval status.');
    } finally {
      setLoading(false);
    }
  };


  // ------------------ Effects ------------------

  useEffect(() => {
    if (!reportingStructureLoaded || !currentUserPosition) {
      setGoalItems([]);
      return;
    }

    if (!user?.organisationUnits?.length) {
      setGoalItems([]);
      return;
    }
    console.log('🔍 [LOADING GOALS] Starting...');
    setLoadingGoals(true);

    // Collect org units from user only (to avoid access issues)
    const allOrgUnits = new Set();
    user.organisationUnits.forEach(ou => allOrgUnits.add(ou.id));

    const userOrgUnits = Array.from(allOrgUnits).join(';');

    // ✅ STEP 2: Fetch ALL goals from these org units (no hardcoded event IDs!)
    const goalPromises = Array.from(allOrgUnits).map(orgUnit =>
      engine.query({
        goals: {
          resource: 'events',
          params: {
            program: ORG_GOALS_PROGRAM,
            programStage: ORG_GOALS_STAGE,
            orgUnit: orgUnit,
            ouMode: 'ACCESSIBLE',
            fields: 'event,orgUnit,dataValues[dataElement,value]',
            pageSize: 1000,
          },
        },
      })
        .then(data => data.goals?.events || [])
        .catch(error => {
          console.warn(`Failed to load goals from ${orgUnit}:`, error);
          return [];
        })
    );

    Promise.all(goalPromises)
      .then(allGoalEvents => {
        const events = allGoalEvents.flat();
        const uniqueGoalEvents = Array.from(
          new Map(events.map(e => [e.event, e])).values()
        );

        const mapped = uniqueGoalEvents.map(event => ({
          id: event.event,
          name: getDataValue(event.dataValues, DATA_ELEMENTS.NAME),
          category: getDataValue(event.dataValues, DATA_ELEMENTS.CATEGORY),
          description: getDataValue(event.dataValues, DATA_ELEMENTS.DESCRIPTION),
          progress: parseInt(getDataValue(event.dataValues, DATA_ELEMENTS.PROGRESS)) || 0,
          startDate: getDataValue(event.dataValues, DATA_ELEMENTS.START_DATE),
          endDate: getDataValue(event.dataValues, DATA_ELEMENTS.END_DATE),
          priority: getDataValue(event.dataValues, DATA_ELEMENTS.PRIORITY),
          orgUnit: event.orgUnit,
        }));

        setGoalItems(mapped);
      })
      .catch(error => {
        console.error('Failed to load company goals:', error);
        setGoalItems([]);
      })
      .finally(() => setLoadingGoals(false));
  }, [user, engine, reportingStructureLoaded, currentUserPosition, reportingToPosition, directReportPositions]);

  useEffect(() => {
    let isMounted = true;

    const checkAll = async () => {
      if (!isMounted || !reportingStructureLoaded) return;

      const protectedSet = new Set();
      const counts = {};

      const allObjectives = [
        ...myObjectives,
        ...directReportObjectives,
        ...reportingToObjectives
      ];

      if (allObjectives.length === 0) {
        if (isMounted) {
          setProtectedObjectives(protectedSet);
          setChildCounts(counts);
        }
        return;
      }

      console.log('🔍 Running child objective checks...');

      // Only check my objectives for now (to reduce API calls)
      const objectivesToCheck = [...myObjectives];

      const checkPromises = objectivesToCheck.map(async (obj) => {
        try {
          const childInfo = await getChildObjectivesInfo(obj.id, engine);

          console.log(`🔍 Checked: "${obj.name}" (${obj.id})`, {
            childCount: childInfo.length,
            children: childInfo.map(c => c.name)
          });

          if (childInfo.length > 0) {
            protectedSet.add(obj.id);
            counts[obj.id] = childInfo.length;
          }

          return { id: obj.id, hasChildren: childInfo.length > 0, count: childInfo.length };
        } catch (error) {
          console.error(`Error checking objective ${obj.id}:`, error);
          // When in doubt, protect it
          protectedSet.add(obj.id);
          return { id: obj.id, hasChildren: true, count: 0 };
        }
      });

      try {
        const results = await Promise.all(checkPromises);

        if (!isMounted) return;

        // Double-check we actually added items
        results.forEach(result => {
          if (result.hasChildren && !protectedSet.has(result.id)) {
            protectedSet.add(result.id);
          }
        });


        setProtectedObjectives(protectedSet);
        setChildCounts(counts);

      } catch (error) {
        console.error('Error in checkAll:', error);
      }
    };

    if (reportingStructureLoaded && myObjectives.length > 0) {
      checkAll();
    }

    return () => {
      isMounted = false;
    };
  }, [myObjectives, engine, reportingStructureLoaded]);



  const getDataElementName = (id) => {
    const dataElements = {
      'EuN5ztFnXsQ': 'Objective Name',
      'nWqW5BrcgP1': 'Description',
      'SlgO60cMK3F': 'Position TEI',
      'plJovUlxfQ0': 'Parent Objective ID',
      'neuilg3L4d2': 'Parent Objective ID',
      'DzQorireRBf': 'Approval Status',
      'KeGWxm1IDYF': 'Approval Remark',
      'eDcVy661j9e': 'Start Date',
      'J0rjxrDKn1l': 'Completion Date',
      'Uhi5GhGQieE': 'Created By',
      'j39sUKa3WpE': 'Current Progress',
      'lLCT66J73BQ': 'Objective Weight',
      'C0K60HlNoCq': 'Objective Category',
      'qPv6D3xAm1u': 'Priority Level',
      'lni65fJi4zC': 'Is Key Objective',
      'CHp6Bd9CR0Q': 'Linked To Higher Level',
      'VhrIQQIVpB9': 'Unknown Reference', // Another one found in logs
    };
    return dataElements[id] || `Unknown (${id})`;
  };

const fetchCompleteObjectiveData = async (objectiveId) => {
  try {
    // Fetch the main objective
    const objectiveResponse = await engine.query({
      objective: {
        resource: 'events',
        id: objectiveId,
        params: {
          fields: 'event,orgUnit,trackedEntityInstance,enrollment,program,programStage,dataValues[dataElement,value]'
        }
      }
    });

    const enrollment = objectiveResponse.objective?.enrollment;
    const program = objectiveResponse.objective?.program;

    console.log('🔍 Objective details:', { 
      enrollment, 
      program,
      programStage: objectiveResponse.objective?.programStage 
    });

    let kras = [];
    let kpis = [];

    if (enrollment) {
      try {
        // Get all events in this enrollment
        const enrollmentResponse = await engine.query({
          enrollmentEvents: {
            resource: 'enrollments',
            id: enrollment,
            params: {
              fields: 'events[event,programStage,dataValues[dataElement,value]]'
            }
          }
        });

        const allEvents = enrollmentResponse.enrollmentEvents?.events || [];
        console.log('📊 All events in enrollment:', allEvents.length);

        // Filter events by program stage
        allEvents.forEach(event => {
          if (event.event === objectiveId) return; // Skip the main objective

          const eventDataValues = {};
          event.dataValues?.forEach(dv => {
            eventDataValues[dv.dataElement] = dv.value;
          });

          
          if (event.programStage === 'BJb3qTgbcTF') {
            kras.push({
              id: event.event,
              name: eventDataValues['obapz6Gnyla'] || '',
              description: eventDataValues['DEECWPTj42E'] || '',
              weight: parseInt(eventDataValues['Xl4VEFoIFIw']) || 0
            });
          }
          
          // Check if it's a KPI (KPI Stage: ivyCLjNLFrd)
          if (event.programStage === 'ivyCLjNLFrd') {
            const rawWeight = eventDataValues['cb87w04djE6'];
            const parsedWeight = parseFloat(rawWeight);
            kpis.push({
              id: event.event,
              name: eventDataValues['DBmylDQA9KX'] || '',
              description: eventDataValues['rQqF75V9t7K'] || '',
              target: eventDataValues['Ru68Ax2Xo2L'] || '',
              weight: Number.isFinite(parsedWeight) ? parsedWeight : ''
            });
          }
        });
      } catch (error) {
        console.log('Error fetching enrollment events:', error);
      }
    }

    // If no enrollment or no events found, try searching by reference
    if (kras.length === 0 && kpis.length === 0) {
      try {
        // Search for KRAs linked to this objective
        const kraResponse = await engine.query({
          kraEvents: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage: 'BJb3qTgbcTF',
              filter: `plJovUlxfQ0:eq:${objectiveId}`,
              fields: 'event,dataValues[dataElement,value]',
              pageSize: 500
            }
          }
        });

        const kraEvents = kraResponse.kraEvents?.events || [];
        console.log('📊 KRAs found by reference:', kraEvents.length);

        kraEvents.forEach(event => {
          const eventDataValues = {};
          event.dataValues?.forEach(dv => {
            eventDataValues[dv.dataElement] = dv.value;
          });

          kras.push({
            id: event.event,
            name: eventDataValues['obapz6Gnyla'] || '',
            description: eventDataValues['DEECWPTj42E'] || '',
            weight: parseInt(eventDataValues['Xl4VEFoIFIw']) || 0
          });
        });

        // Search for KPIs linked to this objective
        const kpiResponse = await engine.query({
          kpiEvents: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage: 'ivyCLjNLFrd',
              filter: `plJovUlxfQ0:eq:${objectiveId}`,
              fields: 'event,dataValues[dataElement,value]',
              pageSize: 500
            }
          }
        });

        const kpiEvents = kpiResponse.kpiEvents?.events || [];
        console.log('📊 KPIs found by reference:', kpiEvents.length);

        kpiEvents.forEach(event => {
          const eventDataValues = {};
          event.dataValues?.forEach(dv => {
            eventDataValues[dv.dataElement] = dv.value;
          });

          kpis.push({
            id: event.event,
            name: eventDataValues['DBmylDQA9KX'] || '',
            description: eventDataValues['rQqF75V9t7K'] || '',
            target: eventDataValues['Ru68Ax2Xo2L'] || '',
            weight: (() => {
              const w = parseFloat(eventDataValues['cb87w04djE6']);
              return Number.isFinite(w) ? w : '';
            })()
          });
        });
      } catch (error) {
        console.log('Error fetching linked KRAs/KPIs:', error);
      }
    }

    // Parse main objective data
    const dataValues = {};
    objectiveResponse.objective?.dataValues?.forEach(dv => {
      dataValues[dv.dataElement] = dv.value;
    });

    console.log('📊 Final counts - KRAs:', kras.length, 'KPIs:', kpis.length);
    if (kras.length > 0) console.log('📊 Sample KRA:', kras[0]);
    if (kpis.length > 0) console.log('📊 Sample KPI:', kpis[0]);

    // Get linked goal ID and name
    const linkedGoalId = dataValues['VhrIQQIVpB9'] || null;
    let linkedGoalName = null;
    if (linkedGoalId && goalItems.length > 0) {
      const goal = goalItems.find(g => g.id === linkedGoalId);
      linkedGoalName = goal?.name || null;
    }

    const objectiveAlignment = dataValues['neuilg3L4d2'] || '';
    let higherLevelObjectiveName = null;
    let higherLevelObjective = null;

    if (objectiveAlignment) {
      try {
        console.log('🔍 [ALIGNED OBJ] Fetching name for aligned objective:', objectiveAlignment);
        
        const alignedResponse = await engine.query({
          alignedObjective: {
            resource: 'events',
            id: objectiveAlignment,
            params: {
              fields: 'event,dataValues[dataElement,value]'
            }
          }
        });

        if (alignedResponse.alignedObjective) {
          const alignedDataValues = {};
          alignedResponse.alignedObjective.dataValues?.forEach(dv => {
            alignedDataValues[dv.dataElement] = dv.value;
          });
          
          higherLevelObjectiveName = alignedDataValues['EuN5ztFnXsQ'] || null;
          higherLevelObjective = {
            id: objectiveAlignment,
            name: higherLevelObjectiveName,
            description: alignedDataValues['nWqW5BrcgP1'] || '',
          };
          
          console.log('✅ [ALIGNED OBJ] Resolved name:', higherLevelObjectiveName);
        } else {
          console.warn('⚠️ [ALIGNED OBJ] Objective not found:', objectiveAlignment);
        }
      } catch (error) {
        console.warn('⚠️ [ALIGNED OBJ] Failed to fetch aligned objective:', error);
        // Fallback: try to find it in loaded manager objectives
        const foundInManager = reportingToObjectives.find(o => o.id === objectiveAlignment);
        if (foundInManager) {
          higherLevelObjectiveName = foundInManager.name;
          higherLevelObjective = foundInManager;
          console.log('✅ [ALIGNED OBJ] Found in reportingToObjectives:', higherLevelObjectiveName);
        }
      }
    }


    return {
      id: objectiveId,
      name: dataValues['EuN5ztFnXsQ'] || 'Unnamed',
      description: dataValues['nWqW5BrcgP1'] || '',
      weight: parseInt(dataValues['yfb9RCRpMB2']) || 0,
      startDate: dataValues['eDcVy661j9e'] || '',
      completionDate: dataValues['J0rjxrDKn1l'] || '',
      status: dataValues['DL7KxNhiV0x'] || '',
      approvalStatus: dataValues['DzQorireRBf'] || 'pending',
      remarks: dataValues['KeGWxm1IDYF'] || '',
      objectiveAlignment: dataValues['neuilg3L4d2'] || '',
      higherLevelObjectiveName: higherLevelObjectiveName,
      higherLevelObjective: higherLevelObjective,
      companyAlignedGoal: linkedGoalId,
      linkedGoalName: linkedGoalName,
      // private notes fields
      managerPrivateNote: dataValues['rJZyovd5akX'] || '',
      noteCreatedBy: dataValues['x56k8a6rLss'] || '',
      noteCreatedAt: dataValues['TlhIr80b8Vp'] || '',

      kras: kras,
      kpis: kpis,
      orgUnit: objectiveResponse.objective?.orgUnit,
      enrollment: enrollment,
      trackedEntityInstance: objectiveResponse.objective?.trackedEntityInstance
    };
  } catch (error) {
    console.error('Error fetching complete objective data:', error);
    return null;
  }
};

  const DeleteButton = ({ objective, onDelete }) => {
    const [isChecking, setIsChecking] = useState(false);

    console.log('🔒 DeleteButton render for:', {
      objectiveId: objective.id,
      objectiveName: objective.name,
      isProtected: protectedObjectives.has(objective.id)
    });

    const deleteResult = canDeleteObjective(objective);
    const { canDelete, reason, type } = deleteResult;

    const linkedGoalId = objective.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;

    // Show protection badge instead of disabled button
    if (type === 'protected') {
      const childCount = getChildCount(objective.id) || 0;
      return (
        <div
          title={`Cannot delete: This objective has ${childCount} aligned objective(s) linked to it`}
          style={{
            backgroundColor: '#fff3cd',
            color: '#856404',
            borderRadius: '4px',
            padding: '4px 4px',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            cursor: 'not-allowed',
            border: '1px solid #ffeaa7',
            height: '20px',
            justifyContent: 'center',
            minWidth: '100px'
          }}
        >
          🔗 {childCount} Aligned{childCount !== 1 ? 's' : ''}
        </div>
      );
    }

    if (!canDelete && type !== 'protected') {
      return (
        <div
          title={`Cannot delete: ${reason}`}
          style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            cursor: 'not-allowed',
            border: '1px solid #ffcdd2',
            height: '20px',
            justifyContent: 'center',
            minWidth: '120px'
          }}
        >
          {type === 'approval' ? '🔒 Locked' : '🔒 Locked'}
        </div>
      );
    }

    return (
      <Button
        small
        destructive
        icon={isChecking ? <CircularLoader small /> : <IconDelete16 />}
        onClick={async () => {
          setIsChecking(true);

          try {
            // Final safety check
            const hasChildren = await checkForChildObjectives(objective.id, engine);

            if (hasChildren) {
              const childInfo = await getChildObjectivesInfo(objective.id, engine);
              const childCount = childInfo.length;

              alert(
                `❌ Cannot Delete Objective\n\n` +
                `This objective has ${childCount} aligned objective(s) linked to it.\n\n` +
                `You must unlink or delete the aligned objectives first.`
              );

              // Update protected set
              setProtectedObjectives(prev => new Set([...prev, objective.id]));
              return;
            }

            if (!window.confirm(`Delete objective "${objective.name}"? This cannot be undone.`)) {
              return;
            }

            await onDelete(objective.orgUnit, objective.id);
          } catch (error) {
            console.error('Delete error:', error);
            alert('Error checking objective status. Please try again.');
          } finally {
            setIsChecking(false);
          }
        }}
        disabled={isChecking}
      >
        {isChecking ? 'Checking...' : 'Delete'}
      </Button>
    );
  };


// Loading state
   if (isLoading) {
       return (
         <div
           style={{
             display: 'flex',
             justifyContent: 'center',
             alignItems: 'center',
             height: '200px',
             flexDirection: 'column',
             gap: '16px',
           }}
         >
           <CircularLoader small />
           <p style={{ color: '#666' }}>Loading objectives and reporting structure...</p>
         </div>
       );
     }
     
     // Empty state - show after loading is complete
     if (shouldShowEmptyState) {
       return (
         <div className="performance-list" style={{ padding: '1rem' }}>
           <h3>{title}</h3>
           <p>No objectives found for you, your manager, or your team.</p>
           {/* Floating Action Button - still show even in empty state */}
           <AddObjectiveButton
             onAdd={onAddObjective}
             reportingToObjectives={reportingToObjectives}
             myObjectives={myObjectives}
             goalItems={goalItems}
             currentUserPosition={currentUserPosition}
             loading={loadingEvents || !reportingStructureLoaded}
             goalsFullyLoaded={goalsFullyLoaded}
             managerObjectivesLoaded={managerObjectivesLoaded}
             protectedObjectives={protectedObjectives}
             childCounts={childCounts}
             creationAllowed={isCreationAllowed}
           />
         </div>
       );
     }
 
     // Main render - this will always execute if the above conditions are not met
     return (
      <div className="performance-list" style={{ padding: '1rem' }}>
        {/* WEIGHT VALIDATION ALERT */}
        {showWeightAlert && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '6px',
              color: '#856404',
              fontWeight: '500',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <span>⚠️</span>
            <span>
              Your objectives total <strong>{totalWeight}%</strong>. You must reach exactly <strong>100%</strong> before creating tasks.
              <br />
              <a style={{ color: '#d39e00', fontWeight: '600' }}>
                Complete your objectives now →
              </a>
            </span>
          </div>
        )}

        {/* TAB NAVIGATION */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '2px solid #e0e0e0',
          paddingBottom: '8px',
          flexWrap: 'wrap'
        }}>
          {/* My Objectives Tab - Always visible */}
          <button
            onClick={() => setActiveTab('my')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === 'my' ? '#2c6693' : 'transparent',
              color: activeTab === 'my' ? 'white' : '#2c6693',
              border: activeTab === 'my' ? 'none' : '1px solid #2c6693',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <NotebookIcon size={16}/> My Objectives
            <span style={{
              backgroundColor: activeTab === 'my' ? 'rgba(255,255,255,0.2)' : '#2c6693',
              color: activeTab === 'my' ? 'white' : 'white',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.8rem'
            }}>
              {myObjectives.length}
            </span>
          </button>

          {/* Manager's Objectives Tab - Only show if there are approved manager objectives */}
          {allManagerObjectivesApproved && approvedReportingToObjectives.length > 0 && (
            <button
              onClick={() => setActiveTab('manager')}
              style={{
                padding: '10px 20px',
                backgroundColor: activeTab === 'manager' ? '#2c6693' : 'transparent',
                color: activeTab === 'manager' ? 'white' : '#2c6693',
                border: activeTab === 'manager' ? 'none' : '1px solid #2c6693',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <UserRoundIcon size={18} /> Manager's Objectives
              <span
                style={{
                  backgroundColor: activeTab === 'manager' ? 'rgba(255,255,255,0.2)' : '#2c6693',
                  color: activeTab === 'manager' ? 'white' : 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                }}
              >
                {approvedReportingToObjectives.length}
              </span>
            </button>
          )}

          {/* Team Objectives Tab - Only show if team objectives exist */}
          {directReportObjectives.length > 0 && (
            <button
              onClick={() => setActiveTab('team')}
              style={{
                padding: '10px 20px',
                backgroundColor: activeTab === 'team' ? '#2c6693' : 'transparent',
                color: activeTab === 'team' ? 'white' : '#2c6693',
                border: activeTab === 'team' ? 'none' : '1px solid #2c6693',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <UsersRoundIcon size={16} /> Team's Objectives
              <span style={{
                backgroundColor: activeTab === 'team' ? 'rgba(255,255,255,0.2)' : '#2c6693',
                color: activeTab === 'team' ? 'white' : 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.8rem'
              }}>
                {directReportObjectives.length}
              </span>
            </button>
          )}
        </div>

        {/* TAB CONTENT */}
        <div>
          {/* ========== MY OBJECTIVES TAB ========== */}
          {activeTab === 'my' && (
            <>
              {myObjectives.length > 0 ? (
                <section
                  style={{
                    backgroundColor: '#e9f8e8ff',
                    borderRadius: '8px',
                    boxShadow: '0 8px 8px rgba(0,0,0,0.1)',
                    padding: '1rem',
                  }}
                >
                  {/* Search and Filter Row */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    marginBottom: '1rem',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <InputField
                        type="text"
                        placeholder="Search my objectives (name, status, dates, goal...)"
                        value={myObjectivesSearch}
                        onChange={({ value }) => setMyObjectivesSearch(value)}
                        dense
                        prefix={<IconSearch24 color="gray" />}
                        suffix={
                          myObjectivesSearch && (
                            <button
                              type="button"
                              onClick={() => setMyObjectivesSearch('')}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px',
                              }}
                              aria-label="Clear search"
                            >
                              <IconCross24 color="gray" />
                            </button>
                          )
                        }
                      />
                    </div>
                    
                    {/* Filter Button */}
                    <div ref={filterButtonRef}>
                      <button
                        onClick={toggleFilterPopover}
                        style={{ 
                          boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                          color: 'white',
                          backgroundColor: 'rgb(24, 38, 233)',
                          borderRadius: '8px',
                          border: 'none',
                          width: '160px',
                          height: '35px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Filter by Goals {selectedGoalFilters.length > 0 
                          ? `(${selectedGoalFilters.length}/${goalItems.length})` 
                          : `(${goalItems.length})`
                        }
                      </button>
                    </div>

                    {/* Filter Popover */}
                    {isFilterPopoverOpen && (
                      <Popover
                        onClickOutside={() => setIsFilterPopoverOpen(false)}
                        reference={filterButtonRef}
                        placement="bottom-end"
                      >
                        <div style={{ 
                          padding: '16px', 
                          minWidth: '250px',
                          maxWidth: '350px',
                          maxHeight: '400px',
                          overflowY: 'auto'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px',
                            borderBottom: '1px solid #e0e0e0',
                            paddingBottom: '8px'
                          }}>
                            <strong>Filter by Company Goals</strong>
                            {selectedGoalFilters.length > 0 && (
                              <button 
                                onClick={clearAllGoalFilters}
                                style={{ 
                                  boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                                  color: 'white',
                                  backgroundColor: '#fd9a06ff',
                                  borderRadius: '8px',
                                  border: 'none',
                                  width: '90px',
                                  height: '35px',
                                  cursor: 'pointer'
                                }}
                              >
                                Clear All
                              </button>
                            )}
                          </div>
                          
                          {loadingGoals ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                              <CircularLoader small />
                            </div>
                          ) : goalItems.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              No goals available
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {goalItems.map(goal => {
                                const isChecked = selectedGoalFilters.includes(goal.id);
                                const objectiveCount = myObjectives.filter(obj => 
                                  getObjectiveGoalId(obj) === goal.id
                                ).length;
                                
                                return (
                                  <div 
                                    key={goal.id}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '4px',
                                      backgroundColor: isChecked ? '#f0f7ff' : 'transparent',
                                      borderRadius: '4px'
                                    }}
                                  >
                                    <Checkbox
                                      checked={isChecked}
                                      label={`${goal.name} (${objectiveCount})`}
                                      onChange={() => handleGoalFilterChange(goal.id)}
                                      dense
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </Popover>
                    )}
                  </div>

                  <h4
                    style={{
                      color: '#2c6693',
                      borderBottom: '2px solid #e6ebf9',
                      paddingBottom: '0.5rem',
                      marginTop: 0,
                      marginBottom: '1rem',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                    }}
                  >
                    My Objectives {(() => {
                      const filtered = filterObjectives(myObjectives, myObjectivesSearch, {
                        section: 'my',
                        goalNameById,
                      });
                      return filtered.length;
                    })()}
                    {selectedGoalFilters.length > 0 && (
                      <span style={{
                        fontSize: '0.85rem',
                        color: '#0b3da1',
                        marginLeft: '10px',
                        fontSize: '1.1rem',
                        fontWeight: '500'
                      }}>
                        (filtered by {selectedGoalFilters.length} goal{selectedGoalFilters.length !== 1 ? 's' : ''})
                      </span>
                    )}
                    {currentUserPosition && (
                      <span
                        style={{
                          fontSize: '0.9rem',
                          color: '#666',
                          marginLeft: '1rem',
                          fontStyle: 'italic',
                        }}
                      >
                        {attributes[POSITION_ATTRIBUTES.POSITION_TITLE]}
                        {isTopLevelUser && ' (Top Level - No Approval Required)'}
                      </span>
                    )}
                  </h4>

                  {(() => {
                    const filtered = filterObjectives(myObjectives, myObjectivesSearch, {
                      section: 'my',
                      goalNameById,
                    });

                    if (filtered.length === 0) {
                      return (
                        <p style={{ fontStyle: 'italic', color: '#666', textAlign: 'center' }}>
                          No matching objectives.
                        </p>
                      );
                    }

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {filtered.map((objective, index) => {
                          const isExpanded = expandedObjectiveId === objective.id;
                          const linkInfo = getLinkedGoalInfo(objective, goalItems, goalNameById, allObjectivesById);

                          return (
                            <div
                              key={objective.id}
                              style={{
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                padding: '16px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                border: '1px solid #e0e0e0',
                              }}
                            >
                              {linkInfo.linkedId && linkInfo.goalName && linkInfo.goalName !== 'No Goal Linked' && (
                                <div style={{
                                  fontSize: '0.85rem',
                                  color: '#b6890c',
                                  fontWeight: '600',
                                  marginBottom: '8px',
                                  padding: '4px 8px',
                                  backgroundColor: '#fff8e1',
                                  borderRadius: '4px',
                                  border: '1px solid #ffecb3',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <span style={{display: 'flex'}}><NotebookIcon size={14} /></span>
                                  <span>Linked Goal: <strong>{linkInfo.goalName}</strong></span>
                                  {linkInfo.isParentObjective && (
                                    <span style={{
                                      fontSize: '0.7rem',
                                      color: '#666',
                                      marginLeft: 'auto',
                                      opacity: 0.7
                                    }}>
                                      via {linkInfo.linkedName}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: linkInfo.exists
                                  ? (isTopLevelUser ? '1fr 1fr 1fr 1fr auto' : '1fr 1fr 1fr 1fr 1fr auto')
                                  : (isTopLevelUser ? '1fr 1fr 1fr auto' : '1fr 1fr 1fr 1fr auto'),
                                alignItems: 'center',
                                gap: '12px',
                                flexWrap: 'wrap',
                              }}>
                                <div>
                                  <strong style={{ color: '#2c6693' }}>Name:</strong>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{objective.name}</span>
                                    {objective.managerPrivateNote && canViewPrivateNote(objective, currentUserPosition) && (
                                      <span 
                                        title="You have a private note on this objective" 
                                        style={{
                                          marginLeft: '4px',
                                          fontSize: '0.7rem',
                                          color: '#666',
                                          display: 'inline-flex',
                                          alignItems: 'center'
                                        }}
                                      >
                                        🔒
                                      </span>
                                    )}
                                    {String(objective.status || '').toLowerCase() === 'draft' && (
                                      <span
                                        style={{
                                          fontSize: '0.72rem',
                                          backgroundColor: '#6c757d',
                                          color: '#fff',
                                          padding: '2px 8px',
                                          borderRadius: '999px',
                                          fontWeight: 600
                                        }}
                                      >
                                        Draft
                                      </span>
                                    )}
                                  </div>
                                </div> 
                                          
                                <div>
                                  <strong style={{ color: '#2c6693' }}>Completion:</strong>
                                  <div>{objective.completionDate}</div>
                                </div>                      
                                {linkInfo.exists && (
                                  <div>
                                    <strong style={{ color: '#2c6693' }}>
                                      {linkInfo.type === 'goal' ? 'Linked Goal:' : 'Aligned with:'}
                                    </strong>
                                    <div style={{
                                      fontSize: '0.9rem',
                                      color: linkInfo.type === 'goal' ? '#b6890c' : '#2c6693',
                                      fontWeight: '500',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {linkInfo.linkedName}
                                    </div>
                                  </div>
                                )}
                                {!isTopLevelUser && (
                                  <div>
                                    <strong style={{ color: '#2c6693' }}>Status:</strong>
                                    <div>{objective.approvalStatus}</div>
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  {!isTopLevelUser && (
                                    <>
                                      {objective.approvalStatus === 'pending' && (
                                        <div
                                          title="Pending"
                                          style={{
                                            backgroundColor: '#FFC107',
                                            borderRadius: '50%',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <IconClock16 />
                                        </div>
                                      )}
                                      {objective.approvalStatus === 'approved' && (
                                        <div
                                          title="Approved"
                                          style={{
                                            backgroundColor: '#4CAF50',
                                            borderRadius: '50%',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                          }}
                                        >
                                          <IconCheckmark16 />
                                        </div>
                                      )}
                                      {objective.approvalStatus === 'returned' && (
                                        <div
                                          title="Returned"
                                          style={{
                                            backgroundColor: '#87CEEB',
                                            borderRadius: '50%',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                          }}
                                        >
                                          <IconInfo16 /> 
                                        </div>
                                      )}
                                    </>
                                  )}
                                  
                                  {/* Edit Button - Completely hidden when locked or approved */}
                                  {!protectedObjectives.has(objective.id) && 
                                  !(!isTopLevelUser && objective.approvalStatus === 'approved') && (
                                    <Button
                                      small
                                      primary
                                      icon={<IconEdit16 />}
                                      onClick={() => {
                                        onEdit(objective.orgUnit, objective, index);
                                      }}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                  
                                  {/* View Button */}
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setIsLoadingObjective(true);
                                      try {
                                        const completeObjective = await fetchCompleteObjectiveData(objective.id);
                                        if (completeObjective) {
                                          setViewModalObjective({ 
                                            objective: completeObjective, 
                                            index: index 
                                          });
                                        } else {
                                          setViewModalObjective({ 
                                            objective: { 
                                              ...objective, 
                                              kras: objective.kras || [], 
                                              kpis: objective.kpis || [],
                                              weight: objective.weight || 0,
                                              linkedGoalName: objective.linkedGoalName || 
                                                            (goalItems.find(g => g.id === objective.companyAlignedGoal)?.name) || 
                                                            null
                                            }, 
                                            index: index 
                                          });
                                        }
                                      } catch (error) {
                                        console.error('Error loading objective details:', error);
                                        setViewModalObjective({ 
                                          objective: { 
                                            ...objective, 
                                            kras: objective.kras || [], 
                                            kpis: objective.kpis || [],
                                            weight: objective.weight || 0,
                                            linkedGoalName: objective.linkedGoalName || 
                                                          (goalItems.find(g => g.id === objective.companyAlignedGoal)?.name) || 
                                                          null
                                          }, 
                                          index: index 
                                        });
                                      } finally {
                                        setIsLoadingObjective(false);
                                      }
                                    }}
                                    style={{
                                      padding: '6px 14px',
                                      backgroundColor: '#04b21e',
                                      color: '#f3f7f3',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      borderColor: 'white',
                                      fontSize: '0.85rem',
                                      fontWeight: 500,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      transition: 'background-color 0.2s',
                                      height: '28px',
                                      marginRight: '4px',
                                      boxShadow: '0 6px 8px rgba(15, 168, 48, 0.5)',
                                    }}
                                    icon={<IconView16 />}
                                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#b8e7bf')}
                                    onMouseLeave={(e) => (e.target.style.backgroundColor = '#04b21e')}
                                    title="View objective details"
                                    disabled={isLoadingObjective}
                                  >
                                    {isLoadingObjective ? '⏳' : ''} {isLoadingObjective ? 'Loading...' : 'View'}
                                  </button>
                                  <DeleteButton objective={objective} onDelete={handleDelete} />
                                  {isTopLevelUser && (
                                    <div
                                      title="Top-level staff - No approval required"
                                      style={{
                                        backgroundColor: '#e6f7ff',
                                        borderRadius: '4px',
                                        padding: '2px 8px',
                                        fontSize: '0.75rem',
                                        color: '#1890ff',
                                        border: '1px solid #91d5ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      ✓ Auto-Approved
                                    </div>
                                  )}
                                </div>
                              </div>
                              {isExpanded && (
                                <div
                                  style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    backgroundColor: '#f9f9f9',
                                    borderRadius: '6px',
                                    border: '1px solid #e0e0e0',
                                  }}
                                >
                                  <div>
                                    <strong>Description:</strong> {objective.description || 'N/A'}
                                  </div>
                                  {/* ✅ ADD KRA/KPA DISPLAY WITH VISIBILITY CHECK */}
                                  {kraEnabled && (
                                    <>
                                      {/* Display KRAs if they exist */}
                                      {objective.kras && objective.kras.length > 0 && (
                                        <div style={{ marginTop: '8px' }}>
                                          <strong>Key Result Areas (KRAs):</strong>
                                          <ul style={{ margin: '4px 0 0 20px', paddingLeft: 0 }}>
                                            {objective.kras.map((kra, idx) => (
                                              <li key={idx} style={{ margin: '2px 0' }}>
                                                {kra.name}
                                                {kra.weight > 0 && ` (${kra.weight}%)`}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      
                                      {/* Display KPIs if they exist */}
                                      {objective.kpis && objective.kpis.length > 0 && (
                                        <div style={{ marginTop: '8px' }}>
                                          <strong>Key Performance Indicators (KPIs):</strong>
                                          <ul style={{ margin: '4px 0 0 20px', paddingLeft: 0 }}>
                                            {objective.kpis.map((kpi, idx) => (
                                              <li key={idx} style={{ margin: '2px 0' }}>
                                                {kpi.name}
                                                {kpi.target && ` → Target: ${kpi.target}`}
                                                {kpi.weight && ` (${kpi.weight}%)`}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {!isTopLevelUser && (
                                    <div style={{ marginTop: '8px' }}>
                                      <strong>Approval Status:</strong> {objective.approvalStatus}
                                    </div>
                                  )}
                                  {objective.approvalStatus === 'returned' && objective.approvalRemark && (
                                    <div style={{ 
                                      marginTop: '8px',
                                      padding: '10px',
                                      backgroundColor: '#fff3cd',
                                      borderRadius: '4px',
                                      border: '1px solid #ffc107',
                                    }}>
                                      <strong style={{ color: '#856404' }}>Manager's Remarks:</strong>
                                      <p style={{ margin: '8px 0 0 0', color: '#856404' }}>{objective.approvalRemark}</p>
                                    </div>
                                  )}
                                  {objective.higherLevelObjectiveId && (
                                    <div
                                      style={{
                                        margin: '10px 0',
                                        padding: '10px',
                                        backgroundColor: '#e8f4fd',
                                        borderRadius: '4px',
                                        border: '1px solid #b3d9ff',
                                      }}
                                    >
                                      <strong style={{ color: '#2c6693' }}>Aligned with:</strong>{' '}
                                      {objective.higherLevelObjectiveId}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </section>
              ) : (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  border: '1px dashed #dee2e6'
                }}>
                  <p style={{ color: '#666', marginBottom: '1rem' }}>
                    You don't have any objectives yet.
                  </p>
                  <AddObjectiveButton
                    onAdd={onAddObjective}
                    reportingToObjectives={reportingToObjectives}
                    myObjectives={myObjectives}
                    goalItems={goalItems}
                    currentUserPosition={currentUserPosition}
                    loading={loadingEvents || !reportingStructureLoaded}
                    goalsFullyLoaded={goalsFullyLoaded}
                    managerObjectivesLoaded={managerObjectivesLoaded}
                    protectedObjectives={protectedObjectives}
                    childCounts={childCounts}
                    creationAllowed={isCreationAllowed}
                    />
                </div>
              )}
            </>
          )}

          {/* ========== MANAGER'S OBJECTIVES TAB ========== */}
          {activeTab === 'manager' && allManagerObjectivesApproved  && approvedReportingToObjectives.length > 0 && (
            <section
              style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                padding: '1rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #2c6693',
                }}
              >
                <h3 style={{ margin: 0, color: '#2c6693', fontSize: '1.2rem', fontWeight: '600' }}>
                  My Manager's Objectives
                </h3>
                <span
                  style={{
                    backgroundColor: '#2c6693',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                  }}
                >
                  {reportingToObjectives.length}
                </span>
              </div>
              <div
                style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: '8px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {approvedReportingToObjectives.map((objective) => {
                    const managerGoalId = objective.dataValues?.find(
                      dv => dv.dataElement === 'VhrIQQIVpB9'
                    )?.value;
                    const managerGoalName = managerGoalId
                      ? goalItems.find(g => g.id === managerGoalId)?.name
                      : null;
                    const isExpanded = expandedObjectiveId === objective.id;
                    const reportingToStaff = getPositionStaff(reportingToPosition, staffList);
                    return (
                      <div
                        key={objective.id}
                        style={{
                          padding: '12px',
                          border: `2px solid ${colors.white}`,
                          borderRadius: '6px',
                          backgroundColor: '#f9f5f5ff',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }}
                      >
                        {managerGoalName && (
                          <div style={{
                            fontSize: '0.85rem',
                            color: '#b6890c',
                            fontWeight: '600',
                            marginBottom: '8px',
                            padding: '4px 8px',
                            backgroundColor: '#fff8e1',
                            borderRadius: '4px',
                            border: '1px solid #ffecb3'
                          }}>
                            Linked Goal: {managerGoalName}
                          </div>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '6px',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <h4
                              style={{
                                margin: 0,
                                color: '#2c6693',
                                fontSize: '1rem',
                                fontWeight: '600',
                                marginBottom: '4px',
                              }}
                            >
                              {objective.name}
                            </h4>
                            <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                              Manager:{' '}
                              {reportingToStaff
                                ? `${reportingToStaff.firstName} ${reportingToStaff.surname}`
                                : 'Unknown'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: '12px' }}>
                            <Button small primary icon={<IconView16 />} onClick={() => toggleExpand(objective.id)}>
                              {isExpanded ? 'Hide' : 'View'}
                            </Button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div
                            style={{
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid #e9ecef',
                            }}
                          >
                            {objective.description && (
                              <div style={{ marginBottom: '8px' }}>
                                <strong style={{ color: '#2c6693', fontSize: '0.9rem' }}>Description:</strong>
                                <p style={{ margin: '4px 0', color: '#495057', fontSize: '0.9rem', lineHeight: '1.4' }}>
                                  {objective.description}
                                </p>
                              </div>
                            )}
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                gap: '0.75rem',
                                fontSize: '0.8rem',
                                marginTop: '8px',
                              }}
                            >
                              <div>
                                <strong style={{ color: '#2c6693' }}>Start Date:</strong>
                                <div>{objective.startDate || 'Not set'}</div>
                              </div>
                              <div>
                                <strong style={{ color: '#2c6693' }}>Completion Date:</strong>
                                <div>{objective.completionDate || 'Not set'}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ========== TEAM'S OBJECTIVES TAB ========== */}
          {activeTab === 'team' && directReportObjectives.length > 0 && (
            <section
              style={{
                backgroundColor: '#d8dffaff',
                borderRadius: '8px',
                boxShadow: '0 8px 8px rgba(0,0,0,0.1)',
                padding: '1rem',
              }}
            >
              {/* Search Input */}
              <InputField
                type="text"
                placeholder="Search team objectives (name, status, staff, position, goal...)"
                value={directReportsSearch}
                onChange={({ value }) => setDirectReportsSearch(value)}
                dense
                prefix={<IconSearch24 color="gray" />}
                suffix={
                  directReportsSearch && (
                    <button
                      type="button"
                      onClick={() => setDirectReportsSearch('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px',
                      }}
                      aria-label="Clear search"
                    >
                      <IconCross24 color="gray" />
                    </button>
                  )
                }
                style={{ width: '100%', marginBottom: '1rem' }}
              />

              <h4
                style={{
                  color: '#2c6693',
                  borderBottom: '2px solid',
                  paddingBottom: '0.5rem',
                  marginTop: 0,
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  My Team's Objectives - Direct Reporting (
                  {(() => {
                    const filtered = filterObjectives(directReportObjectives, directReportsSearch, {
                      section: 'team',
                      goalNameById,
                      staffList,
                      getPositionStaff,
                      extractAttributes,
                      POSITION_ATTRIBUTES,
                    });
                    return filtered.length;
                  })()}
                  )
                </span>
              </h4>

              {(() => {
                const filtered = filterObjectives(directReportObjectives, directReportsSearch, {
                  section: 'team',
                  goalNameById,
                  staffList,
                  getPositionStaff,
                  extractAttributes,
                  POSITION_ATTRIBUTES,
                });

                if (filtered.length === 0) {
                  return (
                    <p style={{ fontStyle: 'italic', color: '#666', textAlign: 'center' }}>
                      No matching team objectives.
                    </p>
                  );
                }

                return (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                      gap: '1rem',
                    }}
                  >
                    {filtered.map((objective) => {
                      const isExpanded = isTeamObjectiveExpanded(objective.id);
                      const staff = getPositionStaff(objective.position, staffList);
                      const positionAttrs = extractAttributes(objective.position.attributes);
                      const positionTitle = positionAttrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown Position';
                      const staffName = staff ? `${staff.firstName} ${staff.surname}` : 'Unknown Staff';
                      const linkInfo = getLinkedGoalInfo(objective, goalItems, goalNameById, allObjectivesById);
                      
                      const weightValue = objective.dataValues?.find(
                        dv => dv.dataElement === 'yfb9RCRpMB2'
                      )?.value || objective.weight || 0;
                      const weight = parseInt(weightValue) || 0;

                      return (
                        <div
                          key={objective.id}
                          style={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '16px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #e0e0e0',
                          }}
                        >
                          {linkInfo.linkedId && linkInfo.goalName && linkInfo.goalName !== 'No Goal Linked' && (
                            <div style={{
                              fontSize: '0.85rem',
                              color: '#b6890c',
                              fontWeight: '600',
                              marginBottom: '8px',
                              padding: '4px 8px',
                              backgroundColor: '#fff8e1',
                              borderRadius: '4px',
                              border: '1px solid #ffecb3',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span><ClipboardCheckIcon size={16}/></span>
                              <span>Linked Goal: <strong>{linkInfo.goalName}</strong></span>
                              {linkInfo.isParentObjective && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  color: '#666',
                                  marginLeft: 'auto',
                                  opacity: 0.7
                                }}>
                                  via {linkInfo.linkedName}
                                </span>
                              )}
                            </div>
                          )}

                          {canViewPrivateNote(objective, currentUserPosition) && objective.managerPrivateNote && (
                            <div style={{
                              marginTop: '8px',
                              marginBottom: '8px',
                              padding: '8px 12px',
                              backgroundColor: '#f0f0f0',
                              borderLeft: `3px solid ${colors.grey600}`,
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              fontStyle: 'italic'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                <span>📝</span>
                                <strong style={{ color: '#666' }}>Your Private Note:</strong>
                              </div>
                              <div style={{ color: '#555', lineHeight: 1.4 }}>
                                {objective.managerPrivateNote}
                              </div>
                              {objective.noteCreatedAt && (
                                <div style={{ fontSize: '0.65rem', color: '#999', marginTop: '4px' }}>
                                  Added: {new Date(objective.noteCreatedAt).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          )}

                          <div style={{ marginBottom: '0.5rem' }}>
                            <h5 style={{ margin: 0, color: '#2c6693', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{objective.name}</span>
                              {objective.managerPrivateNote && canViewPrivateNote(objective, currentUserPosition) && (
                                <span 
                                  title="You have a private note on this objective" 
                                  style={{
                                    marginLeft: '4px',
                                    fontSize: '0.7rem',
                                    color: '#666',
                                    display: 'inline-flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  🔒
                                </span>
                              )}
                              {String(objective.status || '').toLowerCase() === 'draft' && (
                                <span
                                  style={{
                                    fontSize: '0.72rem',
                                    backgroundColor: '#6c757d',
                                    color: '#fff',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    fontWeight: 600
                                  }}
                                >
                                  Draft
                                </span>
                              )}
                            </h5>

                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '0.5rem',
                              marginTop: '0.5rem',
                              padding: '8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e9ecef'
                            }}>
                              <div style={{ fontSize: '0.85rem' }}>
                                <strong>Weight:</strong> {weight}%
                              </div>
                              <div style={{ fontSize: '0.85rem' }}>
                                <strong>Status:</strong> {objective.approvalStatus}
                              </div>
                            </div>

                            <p style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                              <strong>Team Member:</strong> {staffName}
                            </p>
                            <p style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                              <strong>Position:</strong> {positionTitle}
                            </p>

                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '0.5rem',
                              marginTop: '0.5rem',
                              fontSize: '0.8rem',
                              color: '#666'
                            }}>
                              <div>
                                <strong>Start:</strong> {objective.startDate}
                              </div>
                              <div>
                                <strong>Completion:</strong> {objective.completionDate}
                              </div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => toggleTeamObjective(objective.id)}
                              style={{
                                padding: '6px 14px',
                                backgroundColor: '#07911b',
                                color: '#f3f7f3',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                borderColor: 'white',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'background-color 0.2s',
                                height: '28px',
                                marginRight: '4px',
                                boxShadow: '0 6px 8px rgba(15, 168, 48, 0.5)',
                              }}
                            >
                              <IconView16 />  {isExpanded ? 'Hide' : 'View'}
                            </button>

                            {isMyDirectReport(objective) && (
                              <button
                                style={{
                                  padding: '6px 14px',
                                  backgroundColor: '#07911b',
                                  color: '#f3f7f3',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  borderColor: 'white',
                                  fontSize: '0.85rem',
                                  fontWeight: 500,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'background-color 0.2s',
                                  height: '28px',
                                  marginRight: '4px',
                                  boxShadow: '0 6px 8px rgba(15, 168, 48, 0.5)',
                                }}
                                onClick={() => {
                                  setEditingPrivateNoteFor(objective);
                                  setPrivateNoteValue(objective.managerPrivateNote || '');
                                }}
                              >
                                <GiNotebook size={16} /> {objective.managerPrivateNote ? 'Edit Note' : 'Add Note'}
                              </button>
                            )}

                            {objective.approvalStatus === 'pending' && isMyDirectReport(objective) && (
                              <>
                                <Button small primary onClick={() => handleApproval(objective, 'approved')}>
                                  Approve
                                </Button>
                                <Button
                                  small
                                  destructive
                                  onClick={() => handleApproval(objective, 'returned')}
                                >
                                  Decline
                                </Button>
                              </>
                            )}
                          </div>

                          {isExpanded && (
                            <div
                              style={{
                                marginTop: '1rem',
                                padding: '0.75rem',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0',
                              }}
                            >
                              <strong>Description:</strong> {objective.description || 'N/A'}
                              <br />
                              <strong>Weight:</strong> {weight}%
                              <br />
                              <strong>Start Date:</strong> {objective.startDate}
                              <br />
                              <strong>Completion Date:</strong> {objective.completionDate}
                              <br />
                              <strong>Linked Staff:</strong> {staff ? `${staff.firstName} ${staff.surname} (${staff.userCredentials?.username})` : 'Unknown'}
                              {/* KRA/KPA display with visibility check */}
                              {kraEnabled && (
                                <>
                                  {objective.kras && objective.kras.length > 0 && (
                                    <>
                                      <br />
                                      <strong>KRAs:</strong> {objective.kras.map(k => k.name).join(', ')}
                                    </>
                                  )}
                                  {objective.kpis && objective.kpis.length > 0 && (
                                    <>
                                      <br />
                                      <strong>KPIs:</strong> {objective.kpis.map(k => `${k.name}${k.target ? ` (→${k.target})` : ''}`).join(', ')}
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </section>
          )}
        </div>

        {/* Company Goals (Top Level Only) */}
          {isTopLevelUser && (
            <section style={{ marginBottom: 30, marginTop: 30 }}>
              <h3 style={{ marginBottom: '1rem', color: '#b6890cff' }}>
                <GiTargetArrows size={16} /> Company Goals
              </h3>
              {loadingGoals ? (
                <CircularLoader small />
              ) : goalItems.length === 0 ? (
                <div
                  style={{
                    padding: '2rem',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '8px',
                    textAlign: 'center',
                  }}
                >
                  <p style={{ margin: 0, color: '#856404' }}>
                    No company goals found. Please create goals in the Goals Management section.
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1.5rem',
                  }}
                >
                  {goalItems.map((goal) => {
                    const progress = Math.min(Math.max(goal.progress || 0, 0), 100);
                    const progressColor =
                      progress >= 80 ? '#2e7d32' :
                      progress >= 50 ? '#ed6c02' :
                      '#d32f2f';

                    const categoryColor = goal.category ? stringToColor(goal.category) : '#6c757d';
                    

                    return (
                      <div
                        key={goal.id}
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '12px',
                          padding: '1.25rem',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          backgroundColor: '#ffffff',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          cursor: 'default',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                        }}
                      >
                        {/* Category Badge */}
                        {goal.category && (
                          <div
                            style={{
                              display: 'inline-block',
                              backgroundColor: categoryColor,
                              color: '#fff',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              padding: '2px 12px',
                              borderRadius: '20px',
                              alignSelf: 'flex-start',
                              marginBottom: '8px',
                              letterSpacing: '0.5px',
                              textTransform: 'uppercase',
                            }}
                          >
                            {goal.category}
                          </div>
                        )}

                        <h4
                          style={{
                            margin: '0 0 0.25rem 0',
                            color: '#1a237e',
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            lineHeight: 1.3,
                          }}
                        >
                          {goal.name}
                        </h4>

                        <p
                          style={{
                            margin: '0 0 1rem 0',
                            fontSize: '0.9rem',
                            color: '#555',
                            flexGrow: 1,
                            lineHeight: 1.4,
                          }}
                        >
                          {goal.description || 'No description provided'}
                        </p>

                        {/* Progress Bar */}
                        <div style={{ marginBottom: '0.75rem' }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: '0.8rem',
                              color: '#555',
                              marginBottom: '4px',
                            }}
                          >
                            <span>Target</span>
                            <span>{progress}%</span>
                          </div>
                          <div
                            style={{
                              height: '8px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '4px',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${progress}%`,
                                backgroundColor: progressColor,
                                borderRadius: '4px',
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </div>
                        </div>

                        {/* Timeline */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.85rem',
                            color: '#666',
                            marginTop: 'auto',
                            borderTop: '1px solid #f0f0f0',
                            paddingTop: '0.75rem',
                          }}
                        >
                          <span style={{ fontSize: '1rem' }}>📅</span>
                          <span>
                            {goal.startDate
                              ? new Date(goal.startDate).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : 'N/A'}{' '}
                            –{' '}
                            {goal.endDate
                              ? new Date(goal.endDate).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        
        {/* === RENDER VIEW MODAL === */}
        {viewModalObjective && (
          <ObjectiveViewModal
            objective={viewModalObjective.objective}
            index={viewModalObjective.index}
            onClose={() => setViewModalObjective(null)}
            goalItems={goalItems}
            kraEnabled={kraEnabled}
          />
        )}

        {/* Private Note Editor Modal */}
        {editingPrivateNoteFor !== null && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              minWidth: '400px',
              maxWidth: '600px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{ marginTop: 0, color: '#2c6693', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GiNotebook size={24} />
                Private Manager Note
              </h3>
              <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '16px' }}>
                This note is <strong>only visible to you</strong>. The staff member will not see this note.
              </p>
              
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <strong>Objective:</strong>
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}>
                  {editingPrivateNoteFor?.name}
                </div>
              </label>
              
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                <strong>Your Private Note:</strong>
                <textarea
                  value={privateNoteValue}
                  onChange={(e) => setPrivateNoteValue(e.target.value)}
                  rows="5"
                  placeholder="Add private notes, reminders, or observations about this staff member's performance..."
                  style={{
                    padding: '12px',
                    border: `1px solid #ccc`,
                    borderRadius: '4px',
                    fontSize: '0.95rem',
                    resize: 'vertical',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </label>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={() => setEditingPrivateNoteFor(null)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#d00a0a',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const objective = editingPrivateNoteFor;
                    try {
                      setIsLoadingObjective(true);
                      
                      const dataValues = objective.dataValues || [];
                      const filteredDataValues = dataValues.filter(
                        dv => !['rJZyovd5akX', 'x56k8a6rLss', 'TlhIr80b8Vp'].includes(dv.dataElement)
                      );
                      
                      const updatedDataValues = [
                        ...filteredDataValues,
                        { dataElement: 'rJZyovd5akX', value: privateNoteValue },
                        { dataElement: 'x56k8a6rLss', value: currentUserPosition?.trackedEntityInstance || '' },
                        { dataElement: 'TlhIr80b8Vp', value: new Date().toISOString() }
                      ];
                      
                      const eventData = {
                        event: objective.id,
                        program: 'yiT00XKrrHP',
                        programStage: 'VIBtpvkpHFR',
                        orgUnit: objective.orgUnit,
                        eventDate: objective.startDate || new Date().toISOString().split('T')[0],
                        status: 'ACTIVE',
                        dataValues: updatedDataValues,
                      };
                      
                      const authHeader = getAuthHeader();
                      await engine.mutate({
                        resource: `events/${objective.id}`,
                        type: 'update',
                        data: eventData,
                        headers: {
                          'Content-Type': 'application/json',
                          ...authHeader,
                        },
                      });
                      
                      setDirectReportObjectives(prev => 
                        prev.map(obj => 
                          obj.id === objective.id 
                            ? { 
                                ...obj, 
                                managerPrivateNote: privateNoteValue,
                                noteCreatedBy: currentUserPosition?.trackedEntityInstance,
                                noteCreatedAt: new Date().toISOString()
                              }
                            : obj
                        )
                      );
                      
                      setEditingPrivateNoteFor(null);
                      setPrivateNoteValue('');
                      alert('Private note saved successfully!');
                    } catch (error) {
                      console.error('Error saving private note:', error);
                      alert('Failed to save private note.');
                    } finally {
                      setIsLoadingObjective(false);
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#2c6693',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Save Private Note
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {isLoadingObjective && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(255,255,255,0.9)',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            zIndex: 10000
          }}>
            <CircularLoader small />
            <p style={{ marginTop: '10px' }}>Loading objective details...</p>
          </div>
        )}

        {/* Floating Action Button - Only show if there are objectives */}
        {(myObjectives.length > 0 || reportingToObjectives.length > 0 || directReportObjectives.length > 0) && (
          <AddObjectiveButton
              onAdd={onAddObjective}
              reportingToObjectives={reportingToObjectives}
              myObjectives={myObjectives}
              goalItems={goalItems}
              currentUserPosition={currentUserPosition}
              loading={loadingEvents || !reportingStructureLoaded}
              goalsFullyLoaded={goalsFullyLoaded}
              managerObjectivesLoaded={managerObjectivesLoaded}
              protectedObjectives={protectedObjectives}
              childCounts={childCounts}
              creationAllowed={isCreationAllowed}
            />
        )}
      </div>
    );
};


export default ObjectivesList;
